<template>
  <div
    class="deck-builder"
    :class="{ 'is-short': menusShort, 'is-compact': menusCompact }"
  >
    <div class="deck-builder-layout">
      <aside v-show="!menusCompact" class="card-viewer-rail">
        <CardViewer
          :card="hoveredCard"
          :width="260"
          :showInfo="true"
          :foil-choices="hoveredFoilChoices"
          :material-names="deckMaterialNames"
          @update:foil="onViewerFoil"
        />
      </aside>

      <div class="deck-builder-main">
        <div class="editor-stage">
        <div class="editor-main">
        <div class="deck-editor-bar">
        <header class="page-header">
          <div class="header-copy">
            <h1 v-if="menusCompact" class="header-title">
              {{ deckName.trim() || t('deck.myDeck') }}
            </h1>
            <h1 v-else class="visually-hidden">
              {{ deckName.trim() || t('deck.myDeck') }}
            </h1>
            <p class="kicker">{{ t('deck.title') }}</p>
            <div v-if="!menusCompact" class="save-row">
              <div class="save-fields">
                <input
                  v-if="!menusCompact"
                  id="deck-name"
                  v-model="deckName"
                  type="text"
                  class="md-field header-name"
                  :placeholder="t('deck.deckName')"
                  :aria-label="t('deck.deckName')"
                >
                <button
                  type="button"
                  class="md-btn-filled"
                  @click="saveDeck"
                >
                  {{ saveButtonLabel }}
                </button>
                <button
                  type="button"
                  class="md-btn-tonal"
                  :disabled="deckStore.editingId === deckStore.activeId"
                  @click="onUseInDuels(deckStore.editingId || '')"
                >
                  {{ t('deck.useInDuels') }}
                </button>
              </div>
              <p v-if="duelDeckHint" class="duel-deck-hint">{{ duelDeckHint }}</p>
            </div>
          </div>
          <router-link v-if="!menusCompact" to="/deck-builder" class="text-btn">
            {{ t('deck.backToDecks') }}
          </router-link>
        </header>

        </div>

        <div
          class="deck-workspace"
          :class="{ 'fill-viewport': menusCompact }"
        >
          <section
            class="md-panel collection-pane"
            v-show="!stackPanes || mobilePane === 'collection'"
          >
            <div v-if="!menusCompact" class="collection-head">
              <h2>{{ t('deck.collection') }}</h2>
              <button
                v-if="menusCompact"
                type="button"
                class="search-icon-btn"
                :class="{ active: trunkFiltersDirty }"
                :aria-label="t('deck.searchFilters')"
                :aria-expanded="filterModalOpen"
                @click="openFilterModal"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <circle cx="10.5" cy="10.5" r="6.5" />
                  <path d="M16 16l5 5" />
                </svg>
                <span v-if="trunkFiltersDirty" class="search-dot" />
              </button>
            </div>
            <p v-show="!menusCompact" class="support">
              {{ t('deck.ownedOnly') }}
              <router-link to="/packs" class="text-link">
                {{ t('deck.openPacks') }}
              </router-link>
            </p>

            <div v-if="!menusCompact" class="filters">
              <div class="search-field">
                <input
                  id="trunk-search"
                  ref="trunkSearchEl"
                  v-model="searchQuery"
                  type="text"
                  class="md-field"
                  :placeholder="t('deck.search')"
                  :aria-label="t('deck.search')"
                >
                <button
                  v-if="searchQuery"
                  type="button"
                  class="search-clear"
                  :aria-label="t('deck.clearSearch')"
                  @click="clearTrunkSearch"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    aria-hidden="true"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>

              <div class="filter-row">
                <select
                  id="trunk-filter-type"
                  v-model="filterType"
                  class="md-field"
                  :aria-label="t('deck.allTypes')"
                >
                  <option value="">{{ t('deck.allTypes') }}</option>
                  <option value="monster">{{ t('deck.allMonsters') }}</option>
                  <option value="Normal Monster">
                    {{ t('card.type.Normal Monster') }}
                  </option>
                  <option value="Effect Monster">
                    {{ t('card.type.Effect Monster') }}
                  </option>
                  <option value="Flip Effect Monster">
                    {{ t('card.type.Flip Effect Monster') }}
                  </option>
                  <option value="Fusion Monster">
                    {{ t('card.type.Fusion Monster') }}
                  </option>
                  <option value="spell">{{ t('deck.spell') }}</option>
                  <option value="trap">{{ t('deck.trap') }}</option>
                </select>

                <select
                  id="trunk-filter-race"
                  v-model="filterRace"
                  class="md-field"
                  :aria-label="
                    raceFilterKind === 'monster'
                      ? t('deck.allRaces')
                      : t('deck.allSubtypes')
                  "
                >
                  <option value="">
                    {{
                      raceFilterKind === 'monster'
                        ? t('deck.allRaces')
                        : t('deck.allSubtypes')
                    }}
                  </option>
                  <option v-for="r in availableRaces" :key="r" :value="r">
                    {{ t(`card.race.${r}`) }}
                  </option>
                </select>

                <select
                  id="trunk-filter-rarity"
                  v-model="filterRarity"
                  class="md-field"
                  :aria-label="t('deck.allRarities')"
                >
                  <option value="">{{ t('deck.allRarities') }}</option>
                  <option value="common">{{ t('packs.rarity.common') }}</option>
                  <option value="rare">{{ t('packs.rarity.rare') }}</option>
                  <option value="super">{{ t('packs.rarity.super') }}</option>
                  <option value="ultra">{{ t('packs.rarity.ultra') }}</option>
                  <option value="ghost">{{ t('packs.rarity.ghost') }}</option>
                  <option value="helios">{{ t('packs.rarity.helios') }}</option>
                  <option value="nightglass">
                    {{ t('packs.rarity.nightglass') }}
                  </option>
                  <option value="stormwake">
                    {{ t('packs.rarity.stormwake') }}
                  </option>
                </select>

                <select
                  id="trunk-filter-set"
                  v-model="filterSet"
                  class="md-field"
                  :aria-label="t('deck.allSets')"
                >
                  <option value="">{{ t('deck.allSets') }}</option>
                  <option
                    v-for="prefix in ALBUM_PREFIXES"
                    :key="prefix"
                    :value="prefix"
                  >
                    {{ prefix }}
                  </option>
                </select>

                <select
                  id="trunk-sort"
                  v-model="trunkSort"
                  class="md-field"
                  :aria-label="t('deck.sortBy')"
                >
                  <option value="name-asc">{{ t('deck.sortNameAsc') }}</option>
                  <option value="name-desc">{{ t('deck.sortNameDesc') }}</option>
                  <option value="acquired-desc">
                    {{ t('deck.sortAcquiredDesc') }}
                  </option>
                  <option value="acquired-asc">
                    {{ t('deck.sortAcquiredAsc') }}
                  </option>
                  <option value="level-desc">{{ t('deck.sortLevelDesc') }}</option>
                  <option value="level-asc">{{ t('deck.sortLevelAsc') }}</option>
                  <option value="atk-desc">{{ t('deck.sortAtkDesc') }}</option>
                  <option value="atk-asc">{{ t('deck.sortAtkAsc') }}</option>
                  <option value="def-desc">{{ t('deck.sortDefDesc') }}</option>
                  <option value="def-asc">{{ t('deck.sortDefAsc') }}</option>
                  <option value="type">{{ t('deck.sortType') }}</option>
                  <option value="rarity">{{ t('deck.sortRarity') }}</option>
                  <option value="set">{{ t('deck.sortSet') }}</option>
                </select>

                <button
                  v-if="!menusCompact"
                  type="button"
                  class="md-btn-tonal"
                  :class="{ selected: extraFiltersActive || showAllFilters }"
                  :aria-expanded="showAllFilters"
                  :title="
                    showAllFilters
                      ? t('deck.fewerFilters')
                      : t('deck.moreFilters')
                  "
                  @click="showAllFilters = !showAllFilters"
                >
                  {{
                    showAllFilters
                      ? t('deck.fewerFilters')
                      : t('deck.moreFilters')
                  }}
                </button>

                <button
                  type="button"
                  class="md-btn-text"
                  :disabled="!trunkFiltersDirty"
                  :title="t('deck.resetFilters')"
                  @click="resetTrunkFilters"
                >
                  {{ t('deck.resetFilters') }}
                </button>
              </div>

              <div v-show="showAllFilters" class="filter-row">
                <select
                  id="trunk-filter-attribute"
                  v-model="filterAttribute"
                  class="md-field"
                  :aria-label="t('deck.allAttributes')"
                >
                  <option value="">{{ t('deck.allAttributes') }}</option>
                  <option value="DARK">{{ t('card.attr.DARK') }}</option>
                  <option value="LIGHT">{{ t('card.attr.LIGHT') }}</option>
                  <option value="FIRE">{{ t('card.attr.FIRE') }}</option>
                  <option value="WATER">{{ t('card.attr.WATER') }}</option>
                  <option value="EARTH">{{ t('card.attr.EARTH') }}</option>
                  <option value="WIND">{{ t('card.attr.WIND') }}</option>
                </select>

                <select
                  id="trunk-filter-level"
                  v-model="filterLevel"
                  class="md-field"
                  :aria-label="t('deck.allLevels')"
                >
                  <option value="">{{ t('deck.allLevels') }}</option>
                  <option v-for="l in 8" :key="l" :value="l">★ {{ l }}</option>
                </select>

                <select
                  id="trunk-filter-availability"
                  v-model="filterAvailability"
                  class="md-field"
                  :aria-label="t('deck.availabilityAll')"
                >
                  <option value="">{{ t('deck.availabilityAll') }}</option>
                  <option value="free">{{ t('deck.availabilityFree') }}</option>
                  <option value="notInDeck">
                    {{ t('deck.availabilityNotInDeck') }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="allCards.length === 0" class="empty-state">
              <p>{{ t('deck.emptyCollection') }}</p>
              <router-link to="/packs" class="text-link">
                {{ t('deck.openForCards') }}
              </router-link>
            </div>
            <div v-if="menusCompact" class="collection-sprite-grid">
              <DeckCardSprite
                v-for="card in filteredCards"
                :key="card.id"
                :card="trunkSpriteCard(card)"
                :qty="`${getCardCountInDeck(card.id)}/${ownedOf(card.id)}`"
                :materials-ready="fusionMaterialsInDeck(card)"
                hide-hud
                fill-on-hold
                @click="onTrunkAdd(card, $event, false)"
                @fill="onTrunkAdd(card, $event, true)"
                @contextmenu="onTrunkContext(card, $event)"
              />
            </div>
            <div v-else class="collection-list">
              <DeckCardListRow
                v-for="card in filteredCards"
                :key="card.id"
                :card="card"
                :inDeck="getCardCountInDeck(card.id)"
                :owned="ownedOf(card.id)"
                :foil="trunkFoil(card.id)"
                :active="hoveredCard?.id === String(card.id)"
                :materials-ready="fusionMaterialsInDeck(card)"
                :just-sent="sentCardId === String(card.id)"
                :just-denied="deniedCardId === String(card.id)"
                @hover="onTrunkHover(card)"
                @pick="onTrunkAdd(card, $event, false)"
                @fill="onTrunkAdd(card, $event, true)"
                @contextmenu="onTrunkContext(card, $event)"
              />
            </div>
          </section>

          <section
            class="md-panel deck-pane"
            :class="{ 'deck-pane-compact': menusCompact }"
            v-show="!stackPanes || mobilePane === 'deck'"
          >
            <div class="deck-pane-body">
              <div class="deck-board">
                <div v-if="!menusCompact" class="deck-toolbar">
                  <h2>{{ t('deck.currentDeck') }}</h2>
                  <div class="deck-actions">
                    <span class="count">{{ mainDeckCards.length }}/60</span>
                    <span class="min-hint">{{ t('deck.min40') }}</span>
                    <button
                      type="button"
                      class="md-btn-filled"
                      :disabled="ownedMainCopies < 40 || autoBuilding"
                      :title="autoBuildTitle"
                      @click="autoBuildDeck"
                    >
                      {{ t('deck.autoBuild') }}
                    </button>
                    <button
                      type="button"
                      class="md-btn-tonal"
                      :disabled="!canUpgradeFoils"
                      :title="t('deck.bestFoilsHint')"
                      @click="upgradeDeckFoils"
                    >
                      {{ t('deck.bestFoils') }}
                    </button>
                    <button type="button" class="md-btn-tonal" @click="sortDeck">
                      {{ t('deck.sort') }}
                    </button>
                    <button
                      type="button"
                      class="md-btn-tonal"
                      :disabled="!deckListReady"
                      :title="t('deck.deckListHint')"
                      @click="openDeckList"
                    >
                      {{ t('deck.deckList') }}
                    </button>
                    <button
                      type="button"
                      class="md-btn-tonal undo-btn"
                      :disabled="!canUndo"
                      :title="undoButtonTitle"
                      @click="undoDeck"
                    >
                      <span class="undo-label">{{ undoButtonLabel }}</span>
                    </button>
                    <button
                      type="button"
                      class="md-btn-text danger"
                      @click="clearDeck"
                    >
                      {{ t('deck.clear') }}
                    </button>
                  </div>
                </div>

                <div v-if="!menusCompact" class="stat-chips">
                  <span class="chip">
                    <span class="n monster">{{ monsterCount }}</span>
                    {{ t('deck.monsters') }}
                  </span>
                  <span class="chip">
                    <span class="n spell">{{ spellCount }}</span>
                    {{ t('deck.spells') }}
                  </span>
                  <span class="chip">
                    <span class="n trap">{{ trapCount }}</span>
                    {{ t('deck.traps') }}
                  </span>
                </div>

                <div ref="mainGridEl" class="main-deck-grid">
                  <div v-if="mainDeckCards.length === 0" class="empty-state wide">
                    <p class="empty-title">{{ t('deck.emptyMain') }}</p>
                    <p v-show="!menusCompact">{{ t('deck.emptyMainHint') }}</p>
                  </div>
                  <DeckCardSprite
                    v-for="(card, idx) in mainDeckCards"
                    :key="card.id + '-' + idx + '-' + (card.foil || 'common')"
                    :card="card"
                    fluid
                    :pulse="justAddedKeys.has('main-' + idx)"
                    @hover="onDeckHover('main', idx)"
                    @click="onDeckSpriteClick('main', idx)"
                    @contextmenu="onDeckContext('main', idx, $event)"
                  />
                </div>

                <div class="extra-block">
                  <div class="extra-label">
                    <span v-if="!menusCompact">{{ t('deck.extraDeck') }}</span>
                    <span class="min-hint">
                      {{ extraDeckCards.length }}/15
                    </span>
                  </div>
                  <div ref="extraGridEl" class="extra-deck-grid">
                    <div
                      v-if="extraDeckCards.length === 0"
                      class="extra-deck-empty"
                    >
                      <span>{{ t('deck.emptyExtra') }}</span>
                    </div>
                    <DeckCardSprite
                      v-for="(card, idx) in extraDeckCards"
                      :key="card.id + '-' + idx + '-' + (card.foil || 'common')"
                      :card="card"
                      fluid
                      :materials-ready="fusionMaterialsInDeck(card)"
                      :pulse="justAddedKeys.has('extra-' + idx)"
                      @hover="onDeckHover('extra', idx)"
                      @click="onDeckSpriteClick('extra', idx)"
                      @contextmenu="onDeckContext('extra', idx, $event)"
                    />
                  </div>
                </div>
              </div>

            </div>
          </section>
        </div>
        </div>
        <aside v-if="menusCompact" class="land-rail" :class="{ 'is-port': !menusShort }">
          <button
            ref="deckTabEl"
            type="button"
            class="land-btn toggle"
            :class="{ 'tab-flash': tabFlash }"
            :aria-label="paneToggleAction"
            @click="toggleMobilePane"
          >
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M7 7h11M15 4l3 3-3 3M17 17H6M9 14l-3 3 3 3" />
            </svg>
            <span>{{ paneToggleLabel }}</span>
          </button>
          <button type="button" class="land-btn save" @click="saveDeck">
            {{ saveButtonLabel }}
          </button>
          <button
            type="button"
            class="land-btn"
            :disabled="deckStore.editingId === deckStore.activeId"
            @click="onUseInDuels(deckStore.editingId || '')"
          >
            {{ t('deck.useInDuels') }}
          </button>
          <button
            type="button"
            class="land-btn"
            :disabled="!deckListReady"
            :title="t('deck.deckListHint')"
            @click="openDeckList"
          >
            {{ t('deck.deckList') }}
          </button>
          <router-link to="/deck-builder" class="land-btn link">
            {{ t('deck.backToDecks') }}
          </router-link>
          <button
            v-if="mobilePane === 'collection'"
            type="button"
            class="land-btn"
            :class="{ active: trunkFiltersDirty }"
            :title="t('deck.searchFilters')"
            :aria-label="t('deck.searchFilters')"
            @click="openFilterModal"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              aria-hidden="true"
            >
              <circle cx="10.5" cy="10.5" r="6.5" />
              <path d="M16 16l5 5" />
            </svg>
          </button>
          <div class="rail-stats" :aria-label="mainCountLabel">
            <span class="rail-count" :title="t('deck.min40')">
              {{ mainCountLabel }}
              <span class="rail-extra">{{ extraDeckCards.length }}/15</span>
            </span>
            <span class="rail-stat" :title="t('deck.monsters')">
              <span class="n monster">{{ monsterCount }}</span>
            </span>
            <span class="rail-stat" :title="t('deck.spells')">
              <span class="n spell">{{ spellCount }}</span>
            </span>
            <span class="rail-stat" :title="t('deck.traps')">
              <span class="n trap">{{ trapCount }}</span>
            </span>
          </div>
          <div class="rail-actions">
            <button
              type="button"
              class="rail-btn undo"
              :disabled="!canUndo"
              :title="undoButtonTitle"
              :aria-label="undoButtonLabel"
              @click="undoDeck"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9 14 4 9l5-5" />
                <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H12" />
              </svg>
              <span>{{ t('deck.undo') }}</span>
            </button>
            <button
              type="button"
              class="rail-btn"
              :disabled="ownedMainCopies < 40 || autoBuilding"
              :title="autoBuildTitle"
              :aria-label="t('deck.autoBuild')"
              @click="autoBuildDeck"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M4 20 14 10" />
                <path d="m12 8 4 4" />
                <path d="M16.5 4.5 19 7l-1.2 3.2L14.5 8.5z" />
                <path d="M19 4v3h3" />
              </svg>
            </button>
            <button
              type="button"
              class="rail-btn"
              :disabled="!canUpgradeFoils"
              :title="t('deck.bestFoilsHint')"
              :aria-label="t('deck.bestFoils')"
              @click="upgradeDeckFoils"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M6 3h12l4 6-10 12L2 9z" />
                <path d="M2 9h20" />
                <path d="m8 9 4 12 4-12" />
                <path d="M8 9 12 3l4 6" />
              </svg>
            </button>
            <button
              type="button"
              class="rail-btn"
              :title="t('deck.sort')"
              :aria-label="t('deck.sort')"
              @click="sortDeck"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M8 6h13M8 12h9M8 18h5" />
                <path d="M4 10V4l-2 2M4 14v6l-2-2" />
              </svg>
            </button>
            <button
              type="button"
              class="rail-btn danger"
              :title="t('deck.clear')"
              :aria-label="t('deck.clear')"
              @click="clearDeck"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 7h14M9 7V5h6v2M8 7l1 12h6l1-12" />
              </svg>
            </button>
          </div>
        </aside>
        </div>
      </div>
    </div>

    <CardPreviewSheet
      :visible="menusCompact && !!hoveredCard"
      @close="closeViewer"
    >
      <div
        class="deck-sheet-viewer"
        :class="{ short: menusShort }"
      >
        <CardViewer
          :card="hoveredCard"
          fluid
          :width="sheetViewerWidth"
          :showInfo="true"
          :foil-choices="hoveredFoilChoices"
          :material-names="deckMaterialNames"
          @update:foil="onViewerFoil"
        />
      </div>
      <template #footer>
        <div class="deck-sheet-footer">
          <div class="deck-sheet-actions">
            <button
              type="button"
              class="sheet-step minus"
              :disabled="!viewerCanRemove"
              :aria-label="t('deck.removeCopy')"
              @click.stop.prevent="removeFromViewer"
            >
              <span class="sheet-step-icon" aria-hidden="true">−</span>
              <span class="sheet-step-label">{{ t('deck.sheetRemove') }}</span>
            </button>
            <span class="sheet-count" aria-live="polite">
              {{ viewerInDeck }}/{{ viewerOwned }}
            </span>
            <button
              type="button"
              class="sheet-step plus"
              :disabled="!viewerCanAdd"
              :aria-label="t('deck.addCopy')"
              @click.stop.prevent="addFromViewer"
            >
              <span class="sheet-step-icon" aria-hidden="true">+</span>
              <span class="sheet-step-label">{{ t('deck.sheetAdd') }}</span>
            </button>
          </div>
          <button
            v-if="canUndo"
            type="button"
            class="md-btn-tonal sheet-undo"
            :title="undoButtonTitle"
            @click.stop.prevent="undoDeck"
          >
            <span class="undo-label">{{ undoButtonLabel }}</span>
          </button>
        </div>
      </template>
    </CardPreviewSheet>

    <Teleport to="body">
      <div
        v-for="ghost in flyGhosts"
        :key="ghost.id"
        class="trunk-fly-ghost"
        :style="ghost.style"
        aria-hidden="true"
      />
    </Teleport>
    <p class="visually-hidden" aria-live="polite">{{ addLiveText }}</p>

    <Teleport to=".app-shell">
      <div
        v-if="deckListOpen"
        class="deck-list-overlay"
        :class="{ short: menusShort }"
        role="dialog"
        aria-modal="true"
        :aria-label="t('deck.deckList')"
      >
        <div class="deck-list-bar">
          <p class="deck-list-kicker">{{ t('deck.deckList') }}</p>
          <div class="deck-list-toggle" role="group" :aria-label="t('deck.deckListView')">
            <button
              type="button"
              :class="{ on: deckListMode === 'list' }"
              :aria-pressed="deckListMode === 'list'"
              @click="deckListMode = 'list'"
            >
              {{ t('deck.deckListNames') }}
            </button>
            <button
              type="button"
              :class="{ on: deckListMode === 'sprites' }"
              :aria-pressed="deckListMode === 'sprites'"
              @click="deckListMode = 'sprites'"
            >
              {{ t('deck.deckListSprites') }}
            </button>
          </div>
          <button
            type="button"
            class="md-btn-tonal deck-list-share"
            @click="shareDeckList"
          >
            {{ deckListShareLabel }}
          </button>
          <button
            type="button"
            class="md-btn-tonal deck-list-share"
            :disabled="deckListExporting"
            @click="exportDeckImage"
          >
            {{ deckListImageLabel }}
          </button>
          <button
            type="button"
            class="filter-modal-close"
            :aria-label="t('mobile.close')"
            @click="closeDeckList"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="deck-list-stage">
          <div class="deck-list-sheet">
            <h2>{{ deckName.trim() || t('deck.myDeck') }}</h2>
            <p class="deck-list-counts">
              {{ t('deck.deckListMain', { n: mainDeckCards.length }) }}
              ·
              {{ t('deck.deckListExtra', { n: extraDeckCards.length }) }}
            </p>
            <section
              v-for="section in deckListSections"
              :key="section.key"
              class="deck-list-section"
            >
              <h3>
                <span class="n" :class="section.tone">{{ section.total }}</span>
                {{ section.title }}
              </h3>
              <ul v-if="deckListMode === 'list'" class="deck-list-grid">
                <li v-for="row in section.rows" :key="section.key + row.id">
                  <img
                    class="deck-list-art"
                    :src="getLocalCardImageUrl(row.id)"
                    alt=""
                    width="36"
                    height="52"
                    decoding="async"
                  >
                  <span class="qty">{{ row.count }}×</span>
                  <span class="name">{{ row.name }}</span>
                </li>
              </ul>
              <ul v-else class="deck-list-sprite-grid">
                <li
                  v-for="slot in spriteSlots(section)"
                  :key="section.key + slot.key"
                >
                  <img
                    :src="getLocalCardImageUrl(slot.id)"
                    alt=""
                    decoding="async"
                  >
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
      <div
        v-if="filterModalOpen"
        class="filter-modal"
        :class="{ short: menusShort }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="filterModalTitleId"
        @pointerup.self="closeFilterModal"
      >
        <div
          ref="filterModalPanelEl"
          class="filter-modal-panel"
          tabindex="-1"
          @pointerdown.stop
          @pointerup.stop
        >
          <div class="filter-modal-head">
            <h3 :id="filterModalTitleId">{{ t('deck.searchFilters') }}</h3>
            <button
              type="button"
              class="filter-modal-close"
              :aria-label="t('mobile.close')"
              @click="closeFilterModal"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div class="filter-modal-body">
            <div class="search-field">
              <input
                :id="filterModalSearchId"
                ref="filterModalSearchEl"
                v-model="draftSearchQuery"
                type="text"
                class="md-field"
                :readonly="searchUntilTap"
                :placeholder="t('deck.search')"
                :aria-label="t('deck.search')"
                @pointerdown="onFilterSearchPointerDown"
              >
              <button
                v-if="draftSearchQuery"
                type="button"
                class="search-clear"
                :aria-label="t('deck.clearSearch')"
                @click="clearDraftSearch"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <div class="filter-row">
              <select
                v-model="draftFilterType"
                class="md-field"
                :aria-label="t('deck.allTypes')"
              >
                <option value="">{{ t('deck.allTypes') }}</option>
                <option value="monster">{{ t('deck.allMonsters') }}</option>
                <option value="Normal Monster">
                  {{ t('card.type.Normal Monster') }}
                </option>
                <option value="Effect Monster">
                  {{ t('card.type.Effect Monster') }}
                </option>
                <option value="Flip Effect Monster">
                  {{ t('card.type.Flip Effect Monster') }}
                </option>
                <option value="Fusion Monster">
                  {{ t('card.type.Fusion Monster') }}
                </option>
                <option value="spell">{{ t('deck.spell') }}</option>
                <option value="trap">{{ t('deck.trap') }}</option>
              </select>
              <select
                v-model="draftFilterRace"
                class="md-field"
                :aria-label="
                  draftRaceFilterKind === 'monster'
                    ? t('deck.allRaces')
                    : t('deck.allSubtypes')
                "
              >
                <option value="">
                  {{
                    draftRaceFilterKind === 'monster'
                      ? t('deck.allRaces')
                      : t('deck.allSubtypes')
                  }}
                </option>
                <option
                  v-for="r in draftAvailableRaces"
                  :key="r"
                  :value="r"
                >
                  {{ t(`card.race.${r}`) }}
                </option>
              </select>
              <select
                v-model="draftFilterRarity"
                class="md-field"
                :aria-label="t('deck.allRarities')"
              >
                <option value="">{{ t('deck.allRarities') }}</option>
                <option value="common">{{ t('packs.rarity.common') }}</option>
                <option value="rare">{{ t('packs.rarity.rare') }}</option>
                <option value="super">{{ t('packs.rarity.super') }}</option>
                <option value="ultra">{{ t('packs.rarity.ultra') }}</option>
                <option value="ghost">{{ t('packs.rarity.ghost') }}</option>
                <option value="helios">{{ t('packs.rarity.helios') }}</option>
                <option value="nightglass">
                  {{ t('packs.rarity.nightglass') }}
                </option>
                <option value="stormwake">
                  {{ t('packs.rarity.stormwake') }}
                </option>
              </select>
              <select
                v-model="draftFilterSet"
                class="md-field"
                :aria-label="t('deck.allSets')"
              >
                <option value="">{{ t('deck.allSets') }}</option>
                <option
                  v-for="prefix in ALBUM_PREFIXES"
                  :key="prefix"
                  :value="prefix"
                >
                  {{ prefix }}
                </option>
              </select>
              <select
                v-model="draftTrunkSort"
                class="md-field"
                :aria-label="t('deck.sortBy')"
              >
                <option value="name-asc">{{ t('deck.sortNameAsc') }}</option>
                <option value="name-desc">{{ t('deck.sortNameDesc') }}</option>
                <option value="acquired-desc">
                  {{ t('deck.sortAcquiredDesc') }}
                </option>
                <option value="acquired-asc">
                  {{ t('deck.sortAcquiredAsc') }}
                </option>
                <option value="level-desc">{{ t('deck.sortLevelDesc') }}</option>
                <option value="level-asc">{{ t('deck.sortLevelAsc') }}</option>
                <option value="atk-desc">{{ t('deck.sortAtkDesc') }}</option>
                <option value="atk-asc">{{ t('deck.sortAtkAsc') }}</option>
                <option value="def-desc">{{ t('deck.sortDefDesc') }}</option>
                <option value="def-asc">{{ t('deck.sortDefAsc') }}</option>
                <option value="type">{{ t('deck.sortType') }}</option>
                <option value="rarity">{{ t('deck.sortRarity') }}</option>
                <option value="set">{{ t('deck.sortSet') }}</option>
              </select>
              <select
                v-model="draftFilterAttribute"
                class="md-field"
                :aria-label="t('deck.allAttributes')"
              >
                <option value="">{{ t('deck.allAttributes') }}</option>
                <option value="DARK">{{ t('card.attr.DARK') }}</option>
                <option value="LIGHT">{{ t('card.attr.LIGHT') }}</option>
                <option value="FIRE">{{ t('card.attr.FIRE') }}</option>
                <option value="WATER">{{ t('card.attr.WATER') }}</option>
                <option value="EARTH">{{ t('card.attr.EARTH') }}</option>
                <option value="WIND">{{ t('card.attr.WIND') }}</option>
              </select>
              <select
                v-model="draftFilterLevel"
                class="md-field"
                :aria-label="t('deck.allLevels')"
              >
                <option value="">{{ t('deck.allLevels') }}</option>
                <option v-for="l in 8" :key="l" :value="l">★ {{ l }}</option>
              </select>
              <select
                v-model="draftFilterAvailability"
                class="md-field"
                :aria-label="t('deck.availabilityAll')"
              >
                <option value="">{{ t('deck.availabilityAll') }}</option>
                <option value="free">{{ t('deck.availabilityFree') }}</option>
                <option value="notInDeck">
                  {{ t('deck.availabilityNotInDeck') }}
                </option>
              </select>
            </div>
          </div>
          <div class="filter-modal-actions">
            <button
              type="button"
              class="md-btn-text"
              :disabled="!draftFiltersDirty"
              @click="resetDraftFilters"
            >
              {{ t('deck.resetFilters') }}
            </button>
            <button
              type="button"
              class="md-btn-filled"
              @click="applyFilterModal"
            >
              {{ t('deck.applyFilters') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <FoilPickPopover
      v-if="foilPick"
      :rect="foilPick.rect"
      :choices="foilPick.choices"
      :model-value="foilPick.value"
      :label="foilPick.label"
      @update:model-value="onFoilPicked"
      @close="foilPick = null"
    />

    <Teleport to=".app-shell">
      <Transition name="deck-toast">
        <div
          v-if="saveFeedback"
          class="deck-save-toast"
          :class="saveFeedback.kind"
          role="status"
        >
          {{ saveFeedback.text }}
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useDeckStore, tournamentDeckLockId, tournamentDeckLocked } from '../stores/deck'
import { useCollectionStore } from '../stores/collection'
import { useI18n } from '../i18n/t'
import { localizedDesc, localizedName } from '../i18n/localizeCard'
import { api } from '../api/http'
import {
  buildOwnedStandardDeck,
  ownedMainCopyCount,
  type CardWinSample,
} from '../game/autoDeck'
import { useAuthStore } from '../stores/auth'
import {
  activateFirstPlayable,
  getSavedDeckStatus,
  getSlotDeckStatus,
} from '../game/localDeck'
import {
  ALBUM_PREFIXES,
  albumSetRank,
  lobSetNumber,
  setCodeNumber,
  setPrintingCode,
} from '../game/packs'
import { getLocalCardImageUrl } from '../utils/cardImages'
import {
  CARD_FOIL_IDS_BEST_FIRST,
  CARD_FOIL_RANK,
  asCardFoil,
  houseLimits,
  type CardFoilId,
} from '@shared/index'
import CardViewer from '../components/CardViewer.vue'
import CardPreviewSheet from '../components/CardPreviewSheet.vue'
import DeckCardListRow from '../components/DeckCardListRow.vue'
import DeckCardSprite from '../components/DeckCardSprite.vue'
import { materialsCovered, quotedMaterialNames } from '../game/fusionMaterialText'
import FoilPickPopover from '../components/FoilPickPopover.vue'
import type { FoilPickRect } from '../components/FoilPickPopover.vue'
import type { CardViewerData } from '../components/CardViewer.vue'
import { useCompactLayout } from '../composables/useCompactLayout'
import { renderDeckListPng, type DeckListImageMode } from './deckListImage'
import { deckCardCategory, sortDeckCards } from '../game/deckOrder'
// @ts-ignore
import lobData from '@/assets/lob-cards.json'

type CardCategory = 'monster' | 'spell' | 'trap' | 'other'
type RaceFilterKind = 'monster' | 'spell' | 'trap'
const TRUNK_SORTS = [
  'name-asc',
  'name-desc',
  'acquired-desc',
  'acquired-asc',
  'level-desc',
  'level-asc',
  'atk-desc',
  'atk-asc',
  'def-desc',
  'def-asc',
  'type',
  'rarity',
  'set',
] as const
type TrunkSort = (typeof TRUNK_SORTS)[number]
const TRUNK_SORT_KEY = 'deckTrunkSort'
const DEFAULT_TRUNK_SORT: TrunkSort = 'name-asc'

function readStoredTrunkSort(): TrunkSort {
  try {
    const raw = localStorage.getItem(TRUNK_SORT_KEY)
    if (raw && (TRUNK_SORTS as readonly string[]).includes(raw)) {
      return raw as TrunkSort
    }
  } catch {
    /* storage blocked */
  }
  return DEFAULT_TRUNK_SORT
}

const RARITY_RANK: Record<CardFoilId, number> = CARD_FOIL_RANK

const CATEGORY_RANK: Record<CardCategory, number> = {
  monster: 0,
  spell: 1,
  trap: 2,
  other: 3,
}

const { t } = useI18n()
const { menusCompact, menusShort } = useCompactLayout()
const stackPanes = computed(() => menusCompact.value)
const sheetViewerWidth = computed(() => (menusShort.value ? 120 : 140))
const route = useRoute()
const router = useRouter()
const deckStore = useDeckStore()
const collection = useCollectionStore()
const auth = useAuthStore()

// Use the imported LOB card data
const lobCards = lobData.data;

// State
const searchQuery = ref('')
const filterType = ref('')
const filterAttribute = ref('')
const filterLevel = ref('')
const filterRace = ref('')
const filterRarity = ref('')
const filterSet = ref('')
const filterAvailability = ref('')
const showAllFilters = ref(false)
const mobilePane = ref<'collection' | 'deck'>('collection')
const deckTabEl = ref<HTMLButtonElement | null>(null)
const paneToggleLabel = computed(() =>
  mobilePane.value === 'collection' ? t('deck.tabTrunk') : t('deck.tabDeck'),
)
const paneToggleAction = computed(() =>
  mobilePane.value === 'collection' ? t('deck.showDeck') : t('deck.showTrunk'),
)

function toggleMobilePane(): void {
  mobilePane.value = mobilePane.value === 'collection' ? 'deck' : 'collection'
}
const mainGridEl = ref<HTMLElement | null>(null)
const extraGridEl = ref<HTMLElement | null>(null)
const sentCardId = ref('')
const deniedCardId = ref('')
const tabFlash = ref(false)
const addLiveText = ref('')
const justAddedKeys = ref(new Set<string>())
type FlyGhost = { id: number; style: Record<string, string> }
const flyGhosts = ref<FlyGhost[]>([])
let ghostSeq = 0
let sentTimer: ReturnType<typeof setTimeout> | null = null
let deniedTimer: ReturnType<typeof setTimeout> | null = null
let tabFlashTimer: ReturnType<typeof setTimeout> | null = null
let addedTimer: ReturnType<typeof setTimeout> | null = null
let liveTimer: ReturnType<typeof setTimeout> | null = null
const ghostTimers: ReturnType<typeof setTimeout>[] = []
const trunkSort = ref<TrunkSort>(readStoredTrunkSort())
watch(trunkSort, (sort) => {
  try {
    localStorage.setItem(TRUNK_SORT_KEY, sort)
  } catch {
    /* storage blocked */
  }
})
const filterModalOpen = ref(false)
const filterModalTitleId = 'deck-filter-modal-title'
const filterModalSearchId = 'trunk-search-modal'
const filterModalPanelEl = ref<HTMLElement | null>(null)
const trunkSearchEl = ref<HTMLInputElement | null>(null)
const filterModalSearchEl = ref<HTMLInputElement | null>(null)
const searchUntilTap = ref(true)
const draftSearchQuery = ref('')
const draftFilterType = ref('')
const draftFilterAttribute = ref('')
const draftFilterLevel = ref('')
const draftFilterRace = ref('')
const draftFilterRarity = ref('')
const draftFilterSet = ref('')
const draftFilterAvailability = ref('')
const draftTrunkSort = ref<TrunkSort>(DEFAULT_TRUNK_SORT)

const extraFiltersActive = computed(() =>
  Boolean(
    filterAttribute.value ||
    filterLevel.value ||
    filterAvailability.value,
  ),
)

const trunkFiltersDirty = computed(() =>
  Boolean(
    searchQuery.value ||
    filterType.value ||
    filterAttribute.value ||
    filterLevel.value ||
    filterRace.value ||
    filterRarity.value ||
    filterSet.value ||
    filterAvailability.value,
  ),
)

const draftFiltersDirty = computed(() =>
  Boolean(
    draftSearchQuery.value ||
    draftFilterType.value ||
    draftFilterAttribute.value ||
    draftFilterLevel.value ||
    draftFilterRace.value ||
    draftFilterRarity.value ||
    draftFilterSet.value ||
    draftFilterAvailability.value,
  ),
)

function copyLiveFiltersToDraft(): void {
  draftSearchQuery.value = searchQuery.value
  draftFilterType.value = filterType.value
  draftFilterAttribute.value = filterAttribute.value
  draftFilterLevel.value = filterLevel.value
  draftFilterRace.value = filterRace.value
  draftFilterRarity.value = filterRarity.value
  draftFilterSet.value = filterSet.value
  draftFilterAvailability.value = filterAvailability.value
  draftTrunkSort.value = trunkSort.value
}

function applyDraftFilters(): void {
  searchQuery.value = draftSearchQuery.value
  filterType.value = draftFilterType.value
  filterAttribute.value = draftFilterAttribute.value
  filterLevel.value = draftFilterLevel.value
  filterRace.value = draftFilterRace.value
  filterRarity.value = draftFilterRarity.value
  filterSet.value = draftFilterSet.value
  filterAvailability.value = draftFilterAvailability.value
  trunkSort.value = draftTrunkSort.value
}

function resetDraftFilters(): void {
  draftSearchQuery.value = ''
  draftFilterType.value = ''
  draftFilterAttribute.value = ''
  draftFilterLevel.value = ''
  draftFilterRace.value = ''
  draftFilterRarity.value = ''
  draftFilterSet.value = ''
  draftFilterAvailability.value = ''
}

/**
 * Opens the trunk filter sheet. Focus the panel, not the search field —
 * focusing the input pops the mobile keyboard before the player asked.
 */
function openFilterModal(): void {
  copyLiveFiltersToDraft()
  filterModalOpen.value = true
  void nextTick(() => {
    filterModalPanelEl.value?.focus()
  })
}

function closeFilterModal(): void {
  filterModalOpen.value = false
}

function applyFilterModal(): void {
  applyDraftFilters()
  filterModalOpen.value = false
}

function onFilterSearchPointerDown(event: PointerEvent): void {
  if (!searchUntilTap.value) return
  searchUntilTap.value = false
  const el = event.currentTarget as HTMLInputElement
  void nextTick(() => el.focus())
}

/** Empty the trunk search field and keep the caret ready to type again. */
function clearTrunkSearch(): void {
  searchQuery.value = ''
  void nextTick(() => trunkSearchEl.value?.focus())
}

/** Empty the filter-sheet search without popping the keyboard first. */
function clearDraftSearch(): void {
  draftSearchQuery.value = ''
  if (searchUntilTap.value) return
  void nextTick(() => filterModalSearchEl.value?.focus())
}

function onFilterModalKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && deckListOpen.value) {
    closeDeckList()
    return
  }
  if (event.key === 'Escape' && filterModalOpen.value) {
    closeFilterModal()
    return
  }
  if (event.key.toLowerCase() !== 'z') return
  if (!event.ctrlKey && !event.metaKey) return
  if (event.shiftKey || event.altKey) return
  const el = event.target
  if (el instanceof HTMLElement) {
    const tag = el.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable) return
  }
  if (!canUndo.value) return
  event.preventDefault()
  undoDeck()
}

/** Clear search and filters. The sort choice is stored on its own. */
function resetTrunkFilters() {
  searchQuery.value = ''
  filterType.value = ''
  filterAttribute.value = ''
  filterLevel.value = ''
  filterRace.value = ''
  filterRarity.value = ''
  filterSet.value = ''
  filterAvailability.value = ''
  resetDraftFilters()
}

const mainDeckCards = ref<any[]>([])
const extraDeckCards = ref<any[]>([])
const deckName = ref(t('deck.defaultName'))

const UNDO_LIMIT = 40

type DeckUndoStep = {
  main: any[]
  extra: any[]
  /** Set when this step removed one copy, so undo can name it and flash it. */
  removed?: { zone: 'main' | 'extra'; index: number; name: string }
}

const undoStack = ref<DeckUndoStep[]>([])
const canUndo = computed(() => undoStack.value.length > 0)
let skipDeckHistory = false

function lastRemovedName(): string {
  const step = undoStack.value[undoStack.value.length - 1]
  return step?.removed?.name.trim() || ''
}

const undoButtonLabel = computed(() => {
  const name = lastRemovedName()
  return name ? t('deck.undoNamed', { name }) : t('deck.undo')
})

const undoButtonTitle = computed(() => {
  const name = lastRemovedName()
  return name ? t('deck.undoRestoreHint', { name }) : t('deck.undoHint')
})

function cloneDeckList(list: any[]): any[] {
  return list.map((card) => ({ ...card }))
}

function rememberDeck(removed?: DeckUndoStep['removed']): void {
  if (skipDeckHistory) return
  undoStack.value.push({
    main: cloneDeckList(mainDeckCards.value),
    extra: cloneDeckList(extraDeckCards.value),
    removed,
  })
  if (undoStack.value.length > UNDO_LIMIT) {
    undoStack.value.splice(0, undoStack.value.length - UNDO_LIMIT)
  }
}

function clearDeckHistory(): void {
  undoStack.value = []
}
const allCards = computed(() =>
  lobCards.filter((card: { id: string | number }) =>
    collection.ownedCount(card.id) > 0,
  ),
)

/** Monster races, or spell/trap icons when that type is selected. */
const raceFilterKind = computed((): RaceFilterKind => {
  const ft = filterType.value
  if (ft === 'spell' || ft === 'trap') return ft
  return 'monster'
})

const draftRaceFilterKind = computed((): RaceFilterKind => {
  const ft = draftFilterType.value
  if (ft === 'spell' || ft === 'trap') return ft
  return 'monster'
})

function racesForKind(kind: RaceFilterKind): string[] {
  const races = new Set<string>()
  allCards.value.forEach((card) => {
    if (!card.race) return
    if (getCardCategory(card) === kind) races.add(card.race)
  })
  return [...races].sort()
}

const availableRaces = computed(() => racesForKind(raceFilterKind.value))
const draftAvailableRaces = computed(() =>
  racesForKind(draftRaceFilterKind.value),
)

watch(availableRaces, (races) => {
  if (filterRace.value && !races.includes(filterRace.value)) {
    filterRace.value = ''
  }
})

watch(draftAvailableRaces, (races) => {
  if (draftFilterRace.value && !races.includes(draftFilterRace.value)) {
    draftFilterRace.value = ''
  }
})

watch(menusCompact, (compact) => {
  if (!compact) filterModalOpen.value = false
})

watch(filterModalOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) searchUntilTap.value = true
})

const hoveredCard = ref<CardViewerData | null>(null)
type HoverSource =
  | { kind: 'trunk'; cardId: string }
  | { kind: 'main' | 'extra'; index: number }
const hoverSource = ref<HoverSource | null>(null)
const foilPick = ref<{
  value: CardFoilId
  choices: CardFoilId[]
  rect: FoilPickRect
  label: string
} | null>(null)

watch(filterRarity, () => {
  const src = hoverSource.value
  if (src?.kind !== 'trunk' || !hoveredCard.value) return
  hoveredCard.value = {
    ...hoveredCard.value,
    foil: trunkFoil(src.cardId),
  }
})

function cardMatchesSearch(card: any, q: string): boolean {
  if (!q) return true
  const enName = String(card.name || '').toLowerCase()
  const locName = localizedName(card.id, card.name).toLowerCase()
  const enDesc = String(card.desc || '').toLowerCase()
  const locDesc = localizedDesc(card.id, card.desc || '').toLowerCase()
  return (
    enName.includes(q) ||
    locName.includes(q) ||
    enDesc.includes(q) ||
    locDesc.includes(q)
  )
}

function cardMatchesAvailability(card: any): boolean {
  const mode = filterAvailability.value
  if (!mode) return true
  const inDeck = getCardCountInDeck(card.id)
  if (mode === 'notInDeck') return inDeck === 0
  if (mode === 'free') return inDeck < collection.ownedCount(card.id)
  return true
}

/**
 * Trunk thumbnail / viewer foil. When a rarity filter is on, show that print
 * if the player owns it (even if another foil is preferred).
 */
function trunkFoil(cardId: string | number): CardFoilId {
  const rarity = filterRarity.value as CardFoilId | ''
  const id = String(cardId)
  if (rarity && collection.foilCount(id, rarity) > 0) return rarity
  return collection.displayFoil(id)
}

/** Trunk sprite payload: catalog card plus the foil the row would show. */
function trunkSpriteCard(card: any): any {
  return { ...card, foil: trunkFoil(card.id) }
}

function compareNames(a: any, b: any): number {
  const left = localizedName(a.id, a.name)
  const right = localizedName(b.id, b.name)
  return left.localeCompare(right, undefined, { sensitivity: 'base' })
}

/**
 * Compare ATK/DEF/level. Monsters always come before spells and traps.
 */
function compareStat(
  a: any,
  b: any,
  get: (card: any) => number,
  dir: 1 | -1,
): number {
  const aMonster = getCardCategory(a) === 'monster'
  const bMonster = getCardCategory(b) === 'monster'
  if (aMonster !== bMonster) return aMonster ? -1 : 1
  const diff = get(a) - get(b)
  if (diff !== 0) return dir * diff
  return compareNames(a, b)
}

function compareTrunkCards(a: any, b: any): number {
  const sort = trunkSort.value
  if (sort === 'name-asc') return compareNames(a, b)
  if (sort === 'name-desc') return -compareNames(a, b)
  if (sort === 'acquired-desc' || sort === 'acquired-asc') {
    const dir = sort === 'acquired-desc' ? -1 : 1
    const left = collection.acquiredAt[String(a.id)] ?? 0
    const right = collection.acquiredAt[String(b.id)] ?? 0
    if (left !== right) return dir * (left - right)
    return compareNames(a, b)
  }
  if (sort === 'level-desc') {
    return compareStat(a, b, (c) => Number(c.level) || 0, -1)
  }
  if (sort === 'level-asc') {
    return compareStat(a, b, (c) => Number(c.level) || 0, 1)
  }
  if (sort === 'atk-desc') {
    return compareStat(a, b, (c) => Number(c.atk) || 0, -1)
  }
  if (sort === 'atk-asc') {
    return compareStat(a, b, (c) => Number(c.atk) || 0, 1)
  }
  if (sort === 'def-desc') {
    return compareStat(a, b, (c) => Number(c.def) || 0, -1)
  }
  if (sort === 'def-asc') {
    return compareStat(a, b, (c) => Number(c.def) || 0, 1)
  }
  if (sort === 'type') {
    const diff =
      CATEGORY_RANK[getCardCategory(a)] - CATEGORY_RANK[getCardCategory(b)]
    if (diff !== 0) return diff
    return compareNames(a, b)
  }
  if (sort === 'set') {
    const prefix = filterSet.value
    if (prefix) {
      const num =
        setCodeNumber(setPrintingCode(String(a.id), prefix))
        - setCodeNumber(setPrintingCode(String(b.id), prefix))
      if (num !== 0) return num
      return compareNames(a, b)
    }
    const rank = albumSetRank(String(a.id)) - albumSetRank(String(b.id))
    if (rank !== 0) return rank
    const num = lobSetNumber(String(a.id)) - lobSetNumber(String(b.id))
    if (num !== 0) return num
    return compareNames(a, b)
  }
  const rarityDiff =
    RARITY_RANK[trunkFoil(String(a.id))] -
    RARITY_RANK[trunkFoil(String(b.id))]
  if (rarityDiff !== 0) return rarityDiff
  return compareNames(a, b)
}

const filteredCards = computed(() => {
  const q = searchQuery.value.toLowerCase().trim()
  const ft = filterType.value
  const rarity = filterRarity.value as CardFoilId | ''
  const matches = allCards.value.filter((card) => {
    const matchesType =
      !ft ||
      (ft === 'monster' || ft === 'spell' || ft === 'trap'
        ? getCardCategory(card) === ft
        : card.type === ft)
    const matchesAttribute =
      !filterAttribute.value || card.attribute === filterAttribute.value
    const matchesLevel =
      !filterLevel.value || card.level === Number(filterLevel.value)
    const matchesRace = !filterRace.value || card.race === filterRace.value
    const matchesRarity =
      !rarity || collection.foilCount(String(card.id), rarity) > 0
    const matchesSet =
      !filterSet.value
      || setPrintingCode(String(card.id), filterSet.value) !== ''
    return (
      cardMatchesSearch(card, q) &&
      matchesType &&
      matchesAttribute &&
      matchesLevel &&
      matchesRace &&
      matchesRarity &&
      matchesSet &&
      cardMatchesAvailability(card)
    )
  })
  return [...matches].sort(compareTrunkCards)
})

const monsterCount = computed(() => mainDeckCards.value.filter(card => getCardCategory(card) === 'monster').length)
const spellCount = computed(() => mainDeckCards.value.filter(card => getCardCategory(card) === 'spell').length)
const trapCount = computed(() => mainDeckCards.value.filter(card => getCardCategory(card) === 'trap').length)
const ownedMainCopies = computed(() =>
  ownedMainCopyCount(lobCards, collection.counts),
)
const autoBuildTitle = computed(() =>
  ownedMainCopies.value < 40
    ? t('deck.autoNeed', { n: ownedMainCopies.value })
    : t('deck.autoHint'),
)

// Group deck cards by ID with quantities
const groupedDeckCards = computed(() => {
  const cardMap = new Map()
  
  allCards.value.forEach(card => {
    if (cardMap.has(card.id)) {
      cardMap.get(card.id).quantity++
    } else {
      cardMap.set(card.id, { card, quantity: 1 })
    }
  })
  
  return Array.from(cardMap.values()).sort((a, b) => a.card.name.localeCompare(b.card.name))
})

// Methods
const ownedOf = (cardId: string | number): number => collection.ownedCount(cardId)

const takeOwnedCopies = (cards: any[]): any[] => {
  const used: Record<string, number> = {}
  const kept: any[] = []
  for (const card of cards) {
    const id = String(card.id)
    used[id] = (used[id] ?? 0) + 1
    if (used[id] <= collection.ownedCount(id)) kept.push(card)
  }
  return stampDeckFoils(kept)
}

function usedFoilsInList(cards: any[], cardId: string): Partial<Record<CardFoilId, number>> {
  const used: Partial<Record<CardFoilId, number>> = {}
  for (const card of cards) {
    if (String(card.id) !== cardId) continue
    const foil = asCardFoil(card.foil)
    used[foil] = (used[foil] ?? 0) + 1
  }
  return used
}

function stampDeckFoils(cards: any[]): any[] {
  const used: Record<string, Partial<Record<CardFoilId, number>>> = {}
  return cards.map((card) => {
    const id = String(card.id)
    const current = asCardFoil(card.foil)
    const bucket = used[id] ?? {}
    const remaining = collection.foilCount(id, current) - (bucket[current] ?? 0)
    const foil =
      remaining > 0
        ? current
        : collection.nextAvailableFoil(id, bucket) ?? current
    used[id] = { ...bucket, [foil]: (bucket[foil] ?? 0) + 1 }
    return { ...card, foil }
  })
}

const addCardToDeck = (card: any) => {
  if (!canAddCard(card)) return
  const isFusion = isFusionCard(card)
  const target = isFusion ? extraDeckCards : mainDeckCards
  const used = usedFoilsInList(
    [...mainDeckCards.value, ...extraDeckCards.value],
    String(card.id),
  )
  const filtered = filterRarity.value as CardFoilId | ''
  const foil =
    filtered && collection.foilCount(card.id, filtered) - (used[filtered] ?? 0) > 0
      ? filtered
      : collection.nextAvailableFoil(card.id, used)
  if (!foil) return
  rememberDeck()
  target.value.push({ ...card, foil })
}

/**
 * Fill remaining owned copies of this name, up to the copy cap.
 */
function addRemainingCopies(card: any): number {
  if (!canAddCard(card)) return 0
  rememberDeck()
  skipDeckHistory = true
  let added = 0
  try {
    while (canAddCard(card)) {
      const before = getCardCountInDeck(card.id)
      addCardToDeck(card)
      if (getCardCountInDeck(card.id) === before) break
      added += 1
    }
  } finally {
    skipDeckHistory = false
  }
  if (added === 0) undoStack.value.pop()
  return added
}

function addOneCopy(card: any): number {
  if (!canAddCard(card)) return 0
  const before = getCardCountInDeck(card.id)
  addCardToDeck(card)
  return getCardCountInDeck(card.id) > before ? 1 : 0
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function destPoint(fusion: boolean): { x: number; y: number } | null {
  if (stackPanes.value && mobilePane.value !== 'deck') {
    const el = deckTabEl.value
    if (!el) return null
    const r = el.getBoundingClientRect()
    return { x: r.left + r.width / 2 - 18, y: r.top + 6 }
  }
  const grid = fusion ? extraGridEl.value : mainGridEl.value
  if (!grid) return null
  const sprites = grid.querySelectorAll('.deck-card-sprite')
  const last = sprites[sprites.length - 1] as HTMLElement | undefined
  if (last) {
    const r = last.getBoundingClientRect()
    return { x: r.left, y: r.top }
  }
  const r = grid.getBoundingClientRect()
  return { x: r.left + 12, y: r.top + 12 }
}

function markJustAdded(zone: 'main' | 'extra', start: number, n: number): void {
  const keys = new Set<string>()
  for (let i = 0; i < n; i++) keys.add(`${zone}-${start + i}`)
  justAddedKeys.value = keys
  if (addedTimer) clearTimeout(addedTimer)
  addedTimer = setTimeout(() => {
    justAddedKeys.value = new Set()
  }, 520)
}

function playMoveFeedback(srcEl: HTMLElement, card: any, n: number): void {
  const id = String(card.id)
  sentCardId.value = id
  if (sentTimer) clearTimeout(sentTimer)
  sentTimer = setTimeout(() => {
    if (sentCardId.value === id) sentCardId.value = ''
  }, 480)

  if (stackPanes.value) {
    tabFlash.value = true
    if (tabFlashTimer) clearTimeout(tabFlashTimer)
    tabFlashTimer = setTimeout(() => {
      tabFlash.value = false
    }, 520)
  }

  addLiveText.value = t('deck.addedCopies', { n })
  if (liveTimer) clearTimeout(liveTimer)
  liveTimer = setTimeout(() => {
    addLiveText.value = ''
  }, 1200)

  if (prefersReducedMotion() || n <= 0) return

  const art = srcEl.querySelector<HTMLElement>('.row-art') ?? srcEl
  const src = art.getBoundingClientRect()
  void nextTick().then(() => {
    const dest = destPoint(isFusionCard(card))
    if (!dest) return
    const url = getLocalCardImageUrl(card.id)
    for (let i = 0; i < n; i++) {
      const ghostId = ++ghostSeq
      flyGhosts.value.push({
        id: ghostId,
        style: {
          '--sx': `${src.left}px`,
          '--sy': `${src.top}px`,
          '--sw': `${src.width}px`,
          '--sh': `${src.height}px`,
          '--dx': `${dest.x + i * 10}px`,
          '--dy': `${dest.y}px`,
          '--delay': `${i * 55}ms`,
          backgroundImage: `url("${url}")`,
        },
      })
      const timer = setTimeout(() => {
        flyGhosts.value = flyGhosts.value.filter((g) => g.id !== ghostId)
      }, 500 + i * 55)
      ghostTimers.push(timer)
    }
  })
}

function playDeniedFeedback(card: any): void {
  const id = String(card.id)
  deniedCardId.value = id
  if (deniedTimer) clearTimeout(deniedTimer)
  deniedTimer = setTimeout(() => {
    if (deniedCardId.value === id) deniedCardId.value = ''
  }, 300)
}

function isFusionCard(card: any): boolean {
  return Array.isArray(card?.typeline) && card.typeline.includes('Fusion')
}

const deckMaterialNames = computed(() =>
  mainDeckCards.value
    .map((card) => String(card?.name || ''))
    .filter(Boolean),
)

function fusionMaterialsInDeck(card: { desc?: string; typeline?: string[] }): boolean {
  if (!isFusionCard(card)) return false
  return materialsCovered(
    quotedMaterialNames(String(card.desc || '')),
    deckMaterialNames.value,
  )
}

function playCopyCap(card: { id?: string | number }): number {
  const house = houseLimits()[String(card.id ?? '')]
  return house === 1 || house === 2 ? house : 3
}

function canAddCard(card: any): boolean {
  if (!card) return false
  if (getCardCountInDeck(card.id) >= collection.ownedCount(card.id)) return false
  const fusion = isFusionCard(card)
  const target = fusion ? extraDeckCards.value : mainDeckCards.value
  if (fusion && extraDeckCards.value.length >= 15) return false
  if (!fusion && mainDeckCards.value.length >= 60) return false
  if (target.filter((c) => String(c.id) === String(card.id)).length >= playCopyCap(card)) {
    return false
  }
  const used = usedFoilsInList(
    [...mainDeckCards.value, ...extraDeckCards.value],
    String(card.id),
  )
  const filtered = filterRarity.value as CardFoilId | ''
  const foil =
    filtered && collection.foilCount(card.id, filtered) - (used[filtered] ?? 0) > 0
      ? filtered
      : collection.nextAvailableFoil(card.id, used)
  return !!foil
}

function onTrunkAdd(card: any, src: HTMLElement, fill: boolean): void {
  const fusion = isFusionCard(card)
  const start = fusion ? extraDeckCards.value.length : mainDeckCards.value.length
  const added = fill ? addRemainingCopies(card) : addOneCopy(card)
  if (added > 0) {
    markJustAdded(fusion ? 'extra' : 'main', start, added)
    playMoveFeedback(src, card, added)
  } else {
    playDeniedFeedback(card)
  }
  // Hold on a phone fills every remaining copy and leaves the sheet closed.
  // A tap adds one and opens it. Desktop always updates the side viewer.
  if (menusCompact.value && fill) return
  showTrunkCard(card)
}

function onTrunkHover(card: any): void {
  if (menusCompact.value) return
  showTrunkCard(card)
}

function onDeckHover(zone: 'main' | 'extra', index: number): void {
  if (menusCompact.value) return
  showDeckCard(zone, index)
}

function onDeckSpriteClick(zone: 'main' | 'extra', index: number): void {
  if (menusCompact.value) {
    showDeckCard(zone, index)
    return
  }
  removeDeckCopy(zone, index)
}

const getCardCountInDeck = (cardId: string | number): number => {
  const id = String(cardId)
  return (
    mainDeckCards.value.filter((card) => String(card.id) === id).length +
    extraDeckCards.value.filter((card) => String(card.id) === id).length
  )
}

const removeDeckCopy = (
  zone: 'main' | 'extra',
  index: number,
  keepViewer = false,
) => {
  const list = zone === 'main' ? mainDeckCards : extraDeckCards
  if (index < 0 || index >= list.value.length) return
  const card = list.value[index]
  rememberDeck({
    zone,
    index,
    name: String(card?.name || ''),
  })
  list.value.splice(index, 1)
  if (
    !keepViewer &&
    hoverSource.value?.kind === zone &&
    hoverSource.value.index === index
  ) {
    hoveredCard.value = null
    hoverSource.value = null
  }
}

function catalogCardById(cardId: string | number | undefined): any | null {
  if (cardId == null) return null
  const id = String(cardId)
  return allCards.value.find((card) => String(card.id) === id) ?? null
}

function closeViewer(): void {
  hoveredCard.value = null
  hoverSource.value = null
}

function lastCopyIndex(zone: 'main' | 'extra', cardId: string | number): number {
  const id = String(cardId)
  const list = zone === 'main' ? mainDeckCards.value : extraDeckCards.value
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (String(list[i].id) === id) return i
  }
  return -1
}

function retargetViewer(cardId: string): void {
  const extraIdx = lastCopyIndex('extra', cardId)
  if (extraIdx >= 0) {
    showDeckCard('extra', extraIdx)
    return
  }
  const mainIdx = lastCopyIndex('main', cardId)
  if (mainIdx >= 0) {
    showDeckCard('main', mainIdx)
    return
  }
  const trunk = catalogCardById(cardId)
  if (trunk) showTrunkCard(trunk)
  else closeViewer()
}

const viewerCatalogCard = computed(() => catalogCardById(hoveredCard.value?.id))
const viewerInDeck = computed(() =>
  hoveredCard.value ? getCardCountInDeck(hoveredCard.value.id) : 0,
)
const viewerOwned = computed(() =>
  hoveredCard.value ? ownedOf(hoveredCard.value.id) : 0,
)
const viewerCanAdd = computed(() => canAddCard(viewerCatalogCard.value))
const viewerCanRemove = computed(() => viewerInDeck.value > 0)
const mainCountLabel = computed(() => {
  const n = mainDeckCards.value.length
  return `${n}/${n < 40 ? 40 : 60}`
})

function addFromViewer(): void {
  const card = viewerCatalogCard.value
  if (!card) return
  addCardToDeck(card)
}

function removeFromViewer(): void {
  const cardId = hoveredCard.value?.id
  if (!cardId || viewerInDeck.value <= 0) return
  const src = hoverSource.value
  if (src?.kind === 'main' || src?.kind === 'extra') {
    removeDeckCopy(src.kind, src.index, true)
  } else {
    const extraIdx = lastCopyIndex('extra', cardId)
    if (extraIdx >= 0) removeDeckCopy('extra', extraIdx, true)
    else {
      const mainIdx = lastCopyIndex('main', cardId)
      if (mainIdx >= 0) removeDeckCopy('main', mainIdx, true)
    }
  }
  retargetViewer(cardId)
}

function toViewerCard(card: any, foil: CardFoilId): CardViewerData {
  return {
    id: String(card.id),
    name: card.name,
    type: card.type,
    level: card.level,
    typeline: card.typeline,
    race: card.race,
    attribute: card.attribute,
    atk: card.atk,
    def: card.def,
    desc: card.desc,
    card_images: card.card_images,
    foil,
  }
}

const showTrunkCard = (card: any) => {
  hoverSource.value = { kind: 'trunk', cardId: String(card.id) }
  hoveredCard.value = toViewerCard(card, trunkFoil(card.id))
}

const showDeckCard = (zone: 'main' | 'extra', index: number) => {
  const list = zone === 'main' ? mainDeckCards.value : extraDeckCards.value
  const card = list[index]
  if (!card) return
  hoverSource.value = { kind: zone, index }
  hoveredCard.value = toViewerCard(card, asCardFoil(card.foil))
}

function announceDeck(text: string): void {
  addLiveText.value = text
  if (liveTimer) clearTimeout(liveTimer)
  liveTimer = setTimeout(() => {
    addLiveText.value = ''
  }, 1800)
}

function scrollDeckCopyIntoView(zone: 'main' | 'extra', index: number): void {
  void nextTick(() => {
    const grid = zone === 'main' ? mainGridEl.value : extraGridEl.value
    const sprite = grid?.querySelectorAll('.deck-card-sprite')[index]
    if (sprite instanceof HTMLElement) {
      sprite.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  })
}

function undoDeck(): void {
  const step = undoStack.value.pop()
  if (!step) return
  mainDeckCards.value = step.main
  extraDeckCards.value = step.extra
  const removed = step.removed
  const restored = removed
    ? (removed.zone === 'main' ? step.main : step.extra)[removed.index]
    : null
  if (removed && restored) {
    markJustAdded(removed.zone, removed.index, 1)
    showDeckCard(removed.zone, removed.index)
    scrollDeckCopyIntoView(removed.zone, removed.index)
    const name = removed.name.trim()
    announceDeck(name ? t('deck.undoRestored', { name }) : t('deck.undoDone'))
    return
  }
  const src = hoverSource.value
  if (src && (src.kind === 'main' || src.kind === 'extra')) {
    const list = src.kind === 'main' ? step.main : step.extra
    if (list[src.index]) showDeckCard(src.kind, src.index)
    else closeViewer()
  }
  announceDeck(t('deck.undoDone'))
}

/**
 * Rect of the card art the user right-clicked (thumbnail in the trunk).
 */
function cardRectFromEvent(event: MouseEvent): FoilPickRect {
  const el = event.currentTarget as HTMLElement
  const thumb = el.querySelector<HTMLElement>('.row-art')
  return (thumb ?? el).getBoundingClientRect()
}

function foilChoicesForTrunk(cardId: string): CardFoilId[] {
  const owned = collection.ownedFoils(cardId)
  return owned.length >= 2 ? owned : []
}

/**
 * Foils this deck copy can switch to without stealing another copy's print.
 */
function foilChoicesForDeckCopy(
  zone: 'main' | 'extra',
  index: number,
): CardFoilId[] {
  const list = zone === 'main' ? mainDeckCards.value : extraDeckCards.value
  const copy = list[index]
  if (!copy) return []
  const cardId = String(copy.id)
  const owned = collection.ownedFoils(cardId)
  if (owned.length < 2) return []
  const copiesInDeck = [...mainDeckCards.value, ...extraDeckCards.value]
    .filter((c) => String(c.id) === cardId).length
  if (copiesInDeck < 2) return owned
  const current = asCardFoil(copy.foil)
  return owned.filter((foil) => {
    if (foil === current) return true
    const used = usedFoilsInList(
      [...mainDeckCards.value, ...extraDeckCards.value].filter(
        (c) => c !== copy,
      ),
      cardId,
    )
    return collection.foilCount(cardId, foil) - (used[foil] ?? 0) > 0
  })
}

const hoveredFoilChoices = computed((): CardFoilId[] => {
  const src = hoverSource.value
  if (!src) return []
  if (src.kind === 'trunk') return foilChoicesForTrunk(src.cardId)
  return foilChoicesForDeckCopy(src.kind, src.index)
})

function onViewerFoil(foil: CardFoilId): void {
  const src = hoverSource.value
  const card = hoveredCard.value
  if (!src || !card) return
  if (src.kind === 'trunk') {
    collection.setPreferredFoil(card.id, foil)
    hoveredCard.value = { ...card, foil }
    return
  }
  const list = src.kind === 'main' ? mainDeckCards : extraDeckCards
  const copy = list.value[src.index]
  if (!copy) return
  if (asCardFoil(copy.foil) === foil) {
    hoveredCard.value = { ...card, foil }
    return
  }
  rememberDeck()
  list.value[src.index] = { ...copy, foil }
  hoveredCard.value = { ...card, foil }
}

/**
 * Right-click a trunk row to change the preferred rarity for that card.
 */
function onTrunkContext(card: any, event: MouseEvent): void {
  showTrunkCard(card)
  const choices = foilChoicesForTrunk(String(card.id))
  if (choices.length < 2) return
  foilPick.value = {
    value: collection.displayFoil(card.id),
    choices,
    rect: cardRectFromEvent(event),
    label: t('collection.preferredFoil'),
  }
}

/**
 * Right-click a deck copy to change that copy's rarity.
 */
function onDeckContext(
  zone: 'main' | 'extra',
  index: number,
  event: MouseEvent,
): void {
  showDeckCard(zone, index)
  const choices = foilChoicesForDeckCopy(zone, index)
  if (choices.length < 2) return
  const list = zone === 'main' ? mainDeckCards.value : extraDeckCards.value
  const card = list[index]
  if (!card) return
  foilPick.value = {
    value: asCardFoil(card.foil),
    choices,
    rect: cardRectFromEvent(event),
    label: t('deck.copyFoil'),
  }
}

function onFoilPicked(foil: CardFoilId): void {
  foilPick.value = null
  onViewerFoil(foil)
}

// Save Deck method
const saveJustDone = ref(false)
const saveFeedback = ref<{ kind: 'ok' | 'warn'; text: string } | null>(null)
let saveFeedbackTimer: ReturnType<typeof setTimeout> | null = null
let saveButtonTimer: ReturnType<typeof setTimeout> | null = null

function sameDeckList(a: any[], b: any[]): boolean {
  if (a.length !== b.length) return false
  return a.every((card, i) => {
    const other = b[i]
    return (
      String(card?.id) === String(other?.id) &&
      asCardFoil(card?.foil) === asCardFoil(other?.foil)
    )
  })
}

const hasUnsavedChanges = computed(() => {
  const slot = deckStore.editingSlot
  if (!slot) return true
  return (
    !sameDeckList(mainDeckCards.value, slot.mainDeckCards) ||
    !sameDeckList(extraDeckCards.value, slot.extraDeckCards) ||
    deckName.value !== slot.name
  )
})

const saveButtonLabel = computed(() => {
  return saveJustDone.value ? t('deck.saved') : t('deck.save')
})

const duelDeckHint = computed(() => {
  const status = getSavedDeckStatus()
  const activeName = deckStore.activeSlot?.name || t('deck.myDeck')
  const editName = deckStore.editingSlot?.name || t('deck.myDeck')
  const editingOther = deckStore.editingId !== deckStore.activeId
  if (editingOther) {
    return t('deck.hintEditingOther', { edit: editName, active: activeName })
  }
  if (status.reason === 'ok') {
    return hasUnsavedChanges.value
      ? t('deck.hintUnsaved', { name: activeName })
      : ''
  }
  if (status.reason === 'empty') return t('deck.hintEmpty')
  if (status.reason === 'unowned') return t('deck.hintUnowned')
  return t('deck.hintIllegal')
})

function showSaveToast(kind: 'ok' | 'warn', text: string): void {
  saveFeedback.value = { kind, text }
  if (saveFeedbackTimer) clearTimeout(saveFeedbackTimer)
  saveFeedbackTimer = setTimeout(() => {
    saveFeedback.value = null
  }, 4000)
}

function persistEditingList(): void {
  deckStore.saveDeck(mainDeckCards.value, extraDeckCards.value, deckName.value)
  const slot = deckStore.editingSlot
  if (slot && deckName.value !== slot.name) deckName.value = slot.name
}

const saveDeck = () => {
  persistEditingList()
  activateFirstPlayable()
  const slot = deckStore.editingSlot
  const status = getSlotDeckStatus(slot)
  const name = deckName.value.trim() || t('deck.myDeck')
  const activeName = deckStore.activeSlot?.name || t('deck.myDeck')
  if (status.reason === 'ok') {
    const isActive = slot?.id === deckStore.activeId
    showSaveToast(
      'ok',
      isActive
        ? t('deck.savedOk', { name })
        : t('deck.savedOther', { name, active: activeName }),
    )
  } else if (status.reason === 'unowned') {
    showSaveToast('warn', t('deck.savedUnowned'))
  } else {
    showSaveToast('warn', t('deck.savedDraft'))
  }
  saveJustDone.value = true
  if (saveButtonTimer) clearTimeout(saveButtonTimer)
  saveButtonTimer = setTimeout(() => {
    saveJustDone.value = false
  }, 2500)
}

function loadSlot(id: string): void {
  deckStore.setEditingId(id)
  const slot = deckStore.editingSlot
  if (!slot) return
  mainDeckCards.value = takeOwnedCopies(slot.mainDeckCards)
  extraDeckCards.value = takeOwnedCopies(slot.extraDeckCards)
  deckName.value = slot.name
  clearDeckHistory()
}

function confirmDiscardIfNeeded(): boolean {
  if (!hasUnsavedChanges.value) return true
  return window.confirm(t('deck.confirmDiscard'))
}

function routeDeckId(): string | null {
  const id = route.params.deckId
  return typeof id === 'string' && id ? id : null
}

let skipLeaveGuard = false

function openRouteSlot(): void {
  deckStore.ensureIntegrity()
  const id = routeDeckId()
  if (tournamentDeckLocked(id)) {
    skipLeaveGuard = true
    void router.replace({ name: 'deck-builder' })
    return
  }
  if (!id || !deckStore.decks.some((slot) => slot.id === id)) {
    skipLeaveGuard = true
    void router.replace({ name: 'deck-builder' })
    return
  }
  loadSlot(id)
}

function onUseInDuels(id: string): void {
  if (!id) return
  if (id === deckStore.editingId && hasUnsavedChanges.value) {
    persistEditingList()
  }
  const slot = deckStore.decks.find((row) => row.id === id)
  if (getSlotDeckStatus(slot).reason !== 'ok') {
    showSaveToast('warn', t('deck.useNeedLegal'))
    return
  }
  deckStore.setActiveId(id)
  const name = slot?.name || t('deck.myDeck')
  showSaveToast('ok', t('deck.savedOkActive', { name }))
}

// Add clearDeck method
const clearDeck = () => {
  if (mainDeckCards.value.length === 0 && extraDeckCards.value.length === 0) return
  rememberDeck()
  mainDeckCards.value = [];
  extraDeckCards.value = [];
}

const autoBuilding = ref(false)

/**
 * Season win rates for the open meta. Empty when the request fails,
 * in which case the build still respects the banlist and fills at random.
 */
async function loadWinRates(): Promise<Record<string, CardWinSample>> {
  if (!auth.token) return {}
  try {
    const payload = await api<{ cards?: { id?: string; wins?: number; results?: number }[] }>(
      '/meta/cards',
      { token: auth.token, quiet: true },
    )
    const rates: Record<string, CardWinSample> = {}
    for (const row of payload.cards ?? []) {
      if (!row || typeof row.id !== 'string') continue
      const wins = typeof row.wins === 'number' ? row.wins : 0
      const results = typeof row.results === 'number' ? row.results : 0
      rates[row.id] = { wins, results }
    }
    return rates
  } catch {
    return {}
  }
}

/**
 * Replaces the current list with a Standard deck from the owned pool.
 * Copy caps follow the banlist. Cards with a higher season win rate fill first.
 */
const autoBuildDeck = async () => {
  if (ownedMainCopies.value < 40 || autoBuilding.value) return
  autoBuilding.value = true
  rememberDeck()
  try {
    const rates = await loadWinRates()
    const built = buildOwnedStandardDeck(lobCards, collection.counts, rates)
    mainDeckCards.value = stampDeckFoils(built.main)
    extraDeckCards.value = stampDeckFoils(built.extra)
  } finally {
    autoBuilding.value = false
  }
}

type FoilSlot = { zone: 'main' | 'extra'; index: number; foil: CardFoilId }

/** The n rarest owned prints of this card, best first. */
function bestOwnedFoils(cardId: string, n: number): CardFoilId[] {
  const out: CardFoilId[] = []
  for (const foil of CARD_FOIL_IDS_BEST_FIRST) {
    const count = collection.foilCount(cardId, foil)
    for (let i = 0; i < count && out.length < n; i++) out.push(foil)
  }
  return out
}

/**
 * Deck copies that should take a rarer owned print. Copies that already
 * match the best available prints stay put.
 */
function foilSwaps(): FoilSlot[] {
  const groups = new Map<string, FoilSlot[]>()
  const collect = (zone: 'main' | 'extra', list: { id: string | number; foil?: string }[]) => {
    list.forEach((card, index) => {
      const id = String(card.id)
      const slots = groups.get(id) ?? []
      slots.push({ zone, index, foil: asCardFoil(card.foil) })
      groups.set(id, slots)
    })
  }
  collect('main', mainDeckCards.value)
  collect('extra', extraDeckCards.value)

  const swaps: FoilSlot[] = []
  for (const [id, slots] of groups) {
    const desired = bestOwnedFoils(id, slots.length)
    const need = new Map<CardFoilId, number>()
    for (const foil of desired) need.set(foil, (need.get(foil) ?? 0) + 1)

    const leftovers: FoilSlot[] = []
    for (const slot of slots) {
      const left = need.get(slot.foil) ?? 0
      if (left > 0) need.set(slot.foil, left - 1)
      else leftovers.push(slot)
    }
    const missing: CardFoilId[] = []
    for (const [foil, count] of need) {
      for (let i = 0; i < count; i++) missing.push(foil)
    }
    missing.sort((a, b) => CARD_FOIL_RANK[a] - CARD_FOIL_RANK[b])
    leftovers.sort((a, b) => CARD_FOIL_RANK[b.foil] - CARD_FOIL_RANK[a.foil])
    const n = Math.min(leftovers.length, missing.length)
    for (let i = 0; i < n; i++) {
      swaps.push({ ...leftovers[i], foil: missing[i] })
    }
  }
  return swaps
}

const canUpgradeFoils = computed(() => foilSwaps().length > 0)

/** Puts the rarest owned print on each copy already in the deck. */
function upgradeDeckFoils(): void {
  const swaps = foilSwaps()
  if (swaps.length === 0) return
  rememberDeck()
  for (const swap of swaps) {
    const list = swap.zone === 'main' ? mainDeckCards : extraDeckCards
    const copy = list.value[swap.index]
    if (!copy) continue
    list.value[swap.index] = { ...copy, foil: swap.foil }
  }
  const src = hoverSource.value
  if (src && (src.kind === 'main' || src.kind === 'extra')) {
    showDeckCard(src.kind, src.index)
  }
}

interface DeckListRow {
  id: string
  name: string
  count: number
}

interface DeckListSection {
  key: string
  title: string
  tone: 'monster' | 'spell' | 'trap' | 'extra'
  total: number
  rows: DeckListRow[]
}

function collapseDeckLines(cards: { id: string | number; name?: string }[]): DeckListRow[] {
  const order: string[] = []
  const counts = new Map<string, DeckListRow>()
  for (const card of cards) {
    const id = String(card.id)
    const hit = counts.get(id)
    if (hit) {
      hit.count += 1
      continue
    }
    order.push(id)
    counts.set(id, {
      id,
      name: localizedName(id, card.name || id),
      count: 1,
    })
  }
  return order
    .map((id) => counts.get(id)!)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

const deckListOpen = ref(false)
const deckListMode = ref<DeckListImageMode>('list')
const deckListCopied = ref(false)
const deckListExporting = ref(false)
const deckListExportFailed = ref(false)
let deckListCopiedTimer = 0
let deckListExportTimer = 0

const deckListReady = computed(
  () => mainDeckCards.value.length + extraDeckCards.value.length > 0,
)

const deckListSections = computed((): DeckListSection[] => {
  const monsters = mainDeckCards.value.filter(
    (card) => getCardCategory(card) !== 'spell' && getCardCategory(card) !== 'trap',
  )
  const spells = mainDeckCards.value.filter(
    (card) => getCardCategory(card) === 'spell',
  )
  const traps = mainDeckCards.value.filter(
    (card) => getCardCategory(card) === 'trap',
  )
  const sections: DeckListSection[] = []
  const push = (
    key: string,
    title: string,
    tone: DeckListSection['tone'],
    cards: typeof monsters,
  ) => {
    if (cards.length === 0) return
    sections.push({
      key,
      title,
      tone,
      total: cards.length,
      rows: collapseDeckLines(cards),
    })
  }
  push('monster', t('deck.monsters'), 'monster', monsters)
  push('spell', t('deck.spells'), 'spell', spells)
  push('trap', t('deck.traps'), 'trap', traps)
  push('extra', t('deck.extraDeck'), 'extra', extraDeckCards.value)
  return sections
})

const deckListShareLabel = computed(() =>
  deckListCopied.value ? t('deck.deckListCopied') : t('deck.deckListShare'),
)

const deckListImageLabel = computed(() => {
  if (deckListExporting.value) return t('deck.deckListImageBusy')
  if (deckListExportFailed.value) return t('deck.deckListImageFail')
  return t('deck.deckListImage')
})

function spriteSlots(section: DeckListSection): { key: string; id: string }[] {
  const slots: { key: string; id: string }[] = []
  for (const row of section.rows) {
    for (let i = 0; i < row.count; i++) slots.push({ key: `${row.id}-${i}`, id: row.id })
  }
  return slots
}

function deckListPlainText(): string {
  const name = deckName.value.trim() || t('deck.myDeck')
  const lines = [
    name,
    `${t('deck.deckListMain', { n: mainDeckCards.value.length })} · ${t('deck.deckListExtra', { n: extraDeckCards.value.length })}`,
  ]
  for (const section of deckListSections.value) {
    lines.push('', `${section.title} (${section.total})`)
    for (const row of section.rows) lines.push(`${row.count}× ${row.name}`)
  }
  return lines.join('\n')
}

function openDeckList(): void {
  if (!deckListReady.value) return
  deckListCopied.value = false
  deckListExportFailed.value = false
  deckListOpen.value = true
  document.body.style.overflow = 'hidden'
}

function closeDeckList(): void {
  deckListOpen.value = false
  document.body.style.overflow = ''
  if (deckListCopiedTimer) {
    clearTimeout(deckListCopiedTimer)
    deckListCopiedTimer = 0
  }
  if (deckListExportTimer) {
    clearTimeout(deckListExportTimer)
    deckListExportTimer = 0
  }
}

function deckListFileName(name: string): string {
  const safe = name.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '')
  return `${safe || 'deck'}.png`
}

function cssColor(name: string, fallback: string): string {
  const root = document.querySelector('.app-shell') ?? document.documentElement
  const value = getComputedStyle(root).getPropertyValue(name).trim()
  return value || fallback
}

async function exportDeckImage(): Promise<void> {
  if (deckListExporting.value) return
  deckListExporting.value = true
  deckListExportFailed.value = false
  const title = deckName.value.trim() || t('deck.myDeck')
  try {
    const blob = await renderDeckListPng({
      mode: deckListMode.value,
      name: title,
      counts: `${t('deck.deckListMain', { n: mainDeckCards.value.length })} · ${t('deck.deckListExtra', { n: extraDeckCards.value.length })}`,
      sections: deckListSections.value.map((section) => ({
        title: section.title,
        tone: section.tone,
        total: section.total,
        rows: section.rows,
      })),
      colors: {
        surface: cssColor('--md-sys-color-surface', '#131316'),
        onSurface: cssColor('--md-sys-color-on-surface', '#e5e1e6'),
        variant: cssColor('--md-sys-color-on-surface-variant', '#c6c5d0'),
        primary: cssColor('--md-sys-color-primary', '#e8c547'),
        monster: '#e2a06a',
        spell: '#81c784',
        trap: '#ce93d8',
        placeholder: cssColor('--md-sys-color-surface-container-high', '#2a2a2e'),
      },
      imageUrl: (id) => getLocalCardImageUrl(id),
    })
    const file = new File([blob], deckListFileName(title), { type: 'image/png' })
    let canShareFile = false
    try {
      canShareFile = typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })
    } catch {
      canShareFile = false
    }
    if (canShareFile) {
      try {
        await navigator.share({ files: [file], title })
        return
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
      }
    }
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = file.name
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1500)
  } catch {
    deckListExportFailed.value = true
    if (deckListExportTimer) clearTimeout(deckListExportTimer)
    deckListExportTimer = window.setTimeout(() => {
      deckListExportFailed.value = false
    }, 1800)
  } finally {
    deckListExporting.value = false
  }
}

async function shareDeckList(): Promise<void> {
  const text = deckListPlainText()
  const title = deckName.value.trim() || t('deck.myDeck')
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text })
      return
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
    }
  }
  try {
    await navigator.clipboard.writeText(text)
    deckListCopied.value = true
    if (deckListCopiedTimer) clearTimeout(deckListCopiedTimer)
    deckListCopiedTimer = window.setTimeout(() => {
      deckListCopied.value = false
    }, 1600)
  } catch {
    /* The sheet stays on screen so the list can still be screenshotted. */
  }
}

function getCardCategory(card: any): CardCategory {
  return deckCardCategory(card)
}

function sortDeck() {
  const next = sortDeckCards(mainDeckCards.value).filter(
    (card) => getCardCategory(card) !== 'other',
  )
  if (sameDeckList(mainDeckCards.value, next)) return
  rememberDeck()
  mainDeckCards.value = next
}

// Load cards on mount
onMounted(() => {
  openRouteSlot()
  window.addEventListener('keydown', onFilterModalKey)
})

watch(tournamentDeckLockId, () => {
  if (!tournamentDeckLocked(routeDeckId())) return
  skipLeaveGuard = true
  void router.replace({ name: 'deck-builder' })
})

watch(
  () => route.params.deckId,
  (id, prev) => {
    if (prev === undefined || id === prev) return
    if (!confirmDiscardIfNeeded()) {
      skipLeaveGuard = true
      void router.replace({
        name: 'deck-editor',
        params: { deckId: String(prev) },
      })
      return
    }
    openRouteSlot()
  },
)

onBeforeRouteLeave((_to, _from, next) => {
  if (skipLeaveGuard) {
    skipLeaveGuard = false
    next()
    return
  }
  next(confirmDiscardIfNeeded())
})

onUnmounted(() => {
  closeDeckList()
  if (saveFeedbackTimer) clearTimeout(saveFeedbackTimer)
  if (saveButtonTimer) clearTimeout(saveButtonTimer)
  if (sentTimer) clearTimeout(sentTimer)
  if (deniedTimer) clearTimeout(deniedTimer)
  if (tabFlashTimer) clearTimeout(tabFlashTimer)
  if (addedTimer) clearTimeout(addedTimer)
  if (liveTimer) clearTimeout(liveTimer)
  ghostTimers.forEach((timer) => clearTimeout(timer))
  window.removeEventListener('keydown', onFilterModalKey)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.deck-builder {
  min-height: calc(100dvh - var(--app-nav-offset, 64px));
  padding: 24px 16px 48px;
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.deck-builder.is-short {
  padding: 8px 12px 16px;
}

.deck-builder-layout {
  --viewer-rail-width: 280px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  max-width: 1600px;
  margin: 0 auto;
}

.deck-editor-bar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
  background: var(--md-sys-color-surface);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 0;
}

.header-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.page-header .text-btn {
  flex-shrink: 0;
}

.kicker {
  margin: 0;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h2 {
  margin: 0 0 8px;
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
}

.support {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.text-btn,
.text-link {
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  text-decoration: none;
}

.text-btn:hover,
.text-link:hover {
  color: var(--md-sys-color-on-surface);
}

.md-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.search-field {
  position: relative;
}

.search-field .md-field {
  width: 100%;
  padding-right: 40px;
}

.search-clear {
  position: absolute;
  top: 50%;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant, #cac4d0);
  transform: translateY(-50%);
  cursor: pointer;
}

.search-clear:hover {
  background: var(--md-sys-color-surface-container-highest, #2b2930);
  color: var(--md-sys-color-on-surface);
}

.search-clear:focus-visible {
  outline: 2px solid var(--md-sys-color-primary, #e8c547);
  outline-offset: 1px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.md-field {
  min-height: 40px;
  padding: 8px 16px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
}

.md-field:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.header-title {
  margin: 0;
  overflow: hidden;
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-name {
  flex: 1 1 16rem;
  min-width: min(100%, 12rem);
  min-height: 48px;
  font-size: 22px;
  line-height: 28px;
}

.md-btn-filled,
.md-btn-tonal,
.md-btn-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
  cursor: pointer;
  white-space: nowrap;
}

.md-btn-filled {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.md-btn-filled:hover:not(:disabled) {
  filter: brightness(1.08);
}

.md-btn-tonal {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.md-btn-tonal.selected {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.md-btn-text {
  background: transparent;
  color: var(--md-sys-color-primary);
}

.md-btn-text:hover:not(:disabled) {
  background: var(--md-state-layer);
}

.md-btn-text.danger {
  color: var(--md-sys-color-error);
}

.md-btn-filled:disabled,
.md-btn-tonal:disabled,
.md-btn-text:disabled {
  opacity: 0.38;
  cursor: not-allowed;
  filter: none;
}

.empty-state {
  padding: 32px 16px;
  text-align: center;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.empty-state.wide {
  grid-column: 1 / -1;
}

.empty-title {
  margin: 0 0 4px;
  color: var(--md-sys-color-on-surface);
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
}

.duel-deck-hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  color: var(--md-sys-color-on-surface-variant);
}

.deck-save-toast {
  position: fixed;
  left: 50%;
  bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  z-index: 200;
  max-width: min(440px, calc(100vw - 32px));
  padding: 12px 20px;
  border-radius: 8px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-align: center;
  box-shadow: var(--md-elevation-2);
  transform: translateX(-50%);
  pointer-events: none;
}

.deck-save-toast.warn {
  outline: 1px solid var(--md-sys-color-primary);
}

.deck-toast-enter-active,
.deck-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.deck-toast-enter-from,
.deck-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

.card-viewer-rail {
  flex: 0 0 var(--viewer-rail-width);
  width: var(--viewer-rail-width);
  position: sticky;
  top: 1rem;
  align-self: flex-start;
  z-index: 20;
  max-height: calc(100vh - 5rem);
  overflow-y: auto;
}

.deck-builder-main {
  flex: 1;
  min-width: 0;
}

.deck-workspace {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: start;
}

.mobile-pane-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 0;
}

.mobile-pane-tabs .md-btn-tonal {
  flex: 1;
  gap: 6px;
  flex-wrap: wrap;
}

.tab-count {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--md-sys-color-primary);
}

.tab-count.extra {
  color: var(--md-sys-color-on-surface-variant);
}

.mobile-pane-tabs .md-btn-tonal.tab-flash {
  animation: tab-flash 480ms cubic-bezier(0.2, 0, 0, 1);
}

@keyframes tab-flash {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--md-sys-color-primary) 0%, transparent);
  }
  40% {
    box-shadow: 0 0 0 3px var(--md-sys-color-primary);
  }
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--md-sys-color-primary) 0%, transparent);
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.trunk-fly-ghost {
  position: fixed;
  z-index: 260;
  left: 0;
  top: 0;
  width: var(--sw);
  height: var(--sh);
  margin: 0;
  padding: 0;
  border-radius: 4px;
  pointer-events: none;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center 18%;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.4);
  transform-origin: top left;
  animation: trunk-fly 420ms cubic-bezier(0.22, 0.7, 0.2, 1) both;
  animation-delay: var(--delay, 0ms);
}

@keyframes trunk-fly {
  0% {
    transform: translate(var(--sx), var(--sy)) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--dx), var(--dy)) scale(0.42);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mobile-pane-tabs .md-btn-tonal.tab-flash,
  .land-btn.tab-flash,
  .trunk-fly-ghost {
    animation: none;
  }
}

.deck-sheet-viewer {
  display: flex;
  justify-content: center;
}

.deck-sheet-viewer :deep(.card-viewer) {
  width: 100%;
  align-items: center;
}

.deck-sheet-viewer :deep(.card-viewer-image) {
  width: 140px;
  height: 204px;
}

.deck-sheet-viewer.short :deep(.card-viewer-image) {
  width: 120px;
  height: 175px;
}

.deck-sheet-viewer :deep(.card-viewer-detail) {
  width: 100% !important;
}

.deck-sheet-viewer :deep(.card-viewer-name) {
  font-size: 18px;
  line-height: 24px;
}

.deck-sheet-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.deck-sheet-actions {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: 8px;
}

.undo-btn,
.sheet-undo {
  max-width: min(100%, 22rem);
  min-width: 0;
}

.sheet-undo {
  width: 100%;
  max-width: 100%;
}

.undo-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sheet-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 48px;
  padding: 0 10px;
  border-radius: 24px;
  border: 1px solid transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.sheet-step-icon {
  font-size: 20px;
  line-height: 1;
  font-weight: 700;
}

.sheet-step-label {
  white-space: nowrap;
}

.sheet-step.plus {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  box-shadow: var(--md-elevation-1);
}

.sheet-step.minus {
  background: transparent;
  border-color: var(--md-sys-color-outline);
  color: var(--md-sys-color-on-surface);
}

.sheet-step:disabled {
  opacity: 0.38;
  cursor: not-allowed;
  box-shadow: none;
}

.sheet-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.25rem;
  padding: 0 8px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.collection-pane,
.deck-pane {
  min-width: 0;
}

.deck-pane-compact {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.deck-pane-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.deck-board {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  flex: 1;
}

.deck-pane-compact .deck-pane-body {
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
}

.deck-pane-compact .deck-board {
  overflow: hidden;
}

.deck-pane-compact .main-deck-grid {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.deck-rail {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 var(--deck-rail-w, 7.25rem);
  width: var(--deck-rail-w, 7.25rem);
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.rail-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.rail-count {
  width: 100%;
  color: var(--md-sys-color-primary);
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.rail-stat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  min-height: 1.5rem;
  padding: 0 6px;
  border-radius: 6px;
  background: var(--md-sys-color-surface-container-high);
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.rail-stat .n.monster {
  color: #e2a06a;
}

.rail-stat .n.spell {
  color: #81c784;
}

.rail-stat .n.trap {
  color: #ce93d8;
}

.rail-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.rail-btn.undo {
  grid-column: 1 / -1;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.rail-btn.undo span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 40px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  cursor: pointer;
}

.rail-btn.danger {
  background: transparent;
  color: var(--md-sys-color-error);
}

.rail-btn:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.collection-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.collection-head h2 {
  margin: 0;
}

.search-icon-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 0;
  border-radius: 20px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  cursor: pointer;
}

.search-icon-btn.active {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.search-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--md-sys-color-primary);
}

.filter-modal {
  position: fixed;
  inset: 0;
  z-index: 250;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 max(8px, env(safe-area-inset-right))
    max(8px, env(safe-area-inset-bottom))
    max(8px, env(safe-area-inset-left));
  background: var(--md-scrim, rgba(4, 6, 16, 0.82));
}

.filter-modal.short {
  align-items: center;
  padding: max(8px, env(safe-area-inset-top))
    max(12px, env(safe-area-inset-right))
    max(8px, env(safe-area-inset-bottom))
    max(12px, env(safe-area-inset-left));
}

.filter-modal-panel {
  display: flex;
  flex-direction: column;
  width: min(480px, 100%);
  max-height: min(88dvh, 720px);
  overflow: hidden;
  padding: 12px 12px 0;
  border-radius: 16px 16px 8px 8px;
  background: var(--md-sys-color-surface-container, #1f1f23);
  color: var(--md-sys-color-on-surface, #e5e1e6);
  box-shadow: var(--md-elevation-1);
}

.filter-modal-panel:focus {
  outline: none;
}

.filter-modal-panel:focus-visible {
  outline: 2px solid var(--md-sys-color-primary, #e8c547);
  outline-offset: 2px;
}

.filter-modal.short .filter-modal-panel {
  width: min(840px, 100%);
  max-height: calc(100dvh - 16px);
  border-radius: 12px;
}

.filter-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  margin-bottom: 8px;
}

.filter-modal-head h3 {
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
}

.filter-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 18px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  cursor: pointer;
}

.filter-modal-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 8px;
}

.filter-modal-body > .search-field {
  width: 100%;
}

.filter-modal-body > .search-field .md-field[readonly] {
  opacity: 1;
  cursor: text;
}

.filter-modal-body .filter-row .md-field {
  flex: 1 1 140px;
}

.filter-modal.short .filter-modal-body .filter-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.filter-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding: 8px 0 max(12px, env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.deck-workspace.fill-viewport .collection-pane,
.deck-workspace.fill-viewport .deck-pane {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.collection-list,
.collection-sprite-grid {
  display: grid;
  gap: 8px;
  align-content: start;
  flex: 1;
  min-height: 12rem;
  max-height: 38rem;
  padding: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 12px;
  background: var(--md-sys-color-surface);
}

.collection-list {
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
}

.collection-sprite-grid {
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  justify-items: center;
  padding-top: 12px;
}

.collection-list::-webkit-scrollbar,
.collection-sprite-grid::-webkit-scrollbar {
  width: 6px;
}

.collection-list::-webkit-scrollbar-track {
  background: var(--md-sys-color-surface-container-high);
  border-radius: 3px;
}

.collection-list::-webkit-scrollbar-thumb {
  background: var(--md-sys-color-outline-variant);
  border-radius: 3px;
}

.deck-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.deck-toolbar h2 {
  margin: 0;
}

.deck-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.count {
  color: var(--md-sys-color-primary);
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
}

.min-hint {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
}

.stat-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  font-weight: 500;
}

.chip .n {
  font-weight: 500;
}

.chip .n.monster {
  color: #e2a06a;
}

.chip .n.spell {
  color: #81c784;
}

.chip .n.trap {
  color: #ce93d8;
}

.main-deck-grid {
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  min-height: 260px;
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 12px;
  background: var(--md-sys-color-surface);
}

.extra-block {
  margin-bottom: 8px;
}

.extra-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
}

.extra-deck-grid {
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  align-items: start;
  gap: 10px;
  width: 100%;
  min-height: 80px;
  padding: 12px;
  background: var(--md-sys-color-surface);
  border-radius: 12px;
}

.extra-deck-empty {
  grid-column: 1 / -1;
  width: 100%;
  padding: 16px 0;
  text-align: center;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.save-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.save-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
}

@media (min-width: 1280px) {
  .deck-workspace {
    grid-template-columns: minmax(280px, 1fr) minmax(480px, 1.15fr);
  }
}

.deck-workspace.fill-viewport {
  grid-template-columns: 1fr;
  align-items: stretch;
  flex: 1;
  height: auto;
  min-height: 0;
}

.deck-workspace.fill-viewport .md-panel {
  max-height: 100%;
  min-height: 0;
}

.deck-workspace.fill-viewport .collection-list,
.deck-workspace.fill-viewport .collection-sprite-grid {
  max-height: none;
  min-height: 0;
  flex: 1;
}

.deck-builder.is-compact {
  --deck-rail-w: 6.75rem;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: calc(100dvh - var(--app-nav-offset, 64px));
  min-height: 0;
  overflow: hidden;
  padding: 12px 12px 20px;
}

.deck-builder.is-compact .deck-builder-layout {
  flex: 1;
  align-items: stretch;
  width: 100%;
  min-height: 0;
}

.deck-builder.is-compact .deck-builder-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.deck-builder.is-compact .deck-editor-bar {
  position: static;
  flex-shrink: 0;
  gap: 8px;
  padding-bottom: 8px;
}

.deck-builder.is-compact .kicker {
  display: none;
}

.deck-builder.is-compact .header-title {
  display: block;
  width: fit-content;
  max-width: 100%;
  margin: 0;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
}

.deck-builder.is-compact .md-panel {
  padding: 10px;
}

.deck-builder.is-compact.is-short {
  --deck-rail-w: 6.5rem;
  padding: 6px 8px 8px;
}

.editor-stage {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.editor-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.deck-builder.is-compact .editor-stage,
.deck-builder.is-compact .editor-main {
  flex: 1;
}

.deck-builder.is-short .editor-stage {
  flex-direction: row;
  align-items: stretch;
  gap: 6px;
}

.deck-builder.is-short .editor-main {
  flex: 1;
  overflow: hidden;
}

.deck-builder.is-short .page-header,
.deck-builder.is-short .header-copy {
  gap: 0;
}

.deck-builder.is-short .header-title {
  display: block;
  width: fit-content;
  max-width: 100%;
  margin: 0;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
}

.land-rail {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 8.25rem;
  width: 8.25rem;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.land-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  min-height: 28px;
  margin: 0;
  padding: 2px 6px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
  font: inherit;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: 0.01em;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.land-btn span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.land-btn.toggle {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
  font-weight: 700;
}

.land-btn.save {
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.land-btn.link {
  background: transparent;
  color: var(--md-sys-color-primary);
}

.land-btn.active {
  box-shadow: inset 0 0 0 1px var(--md-sys-color-primary);
}

.land-btn:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

.land-btn.tab-flash {
  animation: tab-flash 480ms cubic-bezier(0.2, 0, 0, 1);
}

.land-rail .rail-count {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
}

.land-rail .rail-extra {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 11px;
  font-weight: 600;
}

.deck-builder.is-short .land-rail .rail-actions {
  gap: 4px;
}

.deck-builder.is-short .land-rail .rail-btn {
  min-height: 28px;
}

.deck-builder.is-compact:not(.is-short) {
  padding: 8px 12px 8px;
}

.deck-builder.is-compact:not(.is-short) .editor-stage {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.deck-builder.is-compact:not(.is-short) .editor-main {
  display: contents;
}

.deck-builder.is-compact:not(.is-short) .deck-editor-bar {
  grid-row: 1;
  gap: 0;
  padding-bottom: 4px;
  position: sticky;
  top: 0;
  z-index: 5;
  background: var(--md-sys-color-surface);
}

.deck-builder.is-compact:not(.is-short) .page-header,
.deck-builder.is-compact:not(.is-short) .header-copy {
  gap: 0;
}

.deck-builder.is-compact:not(.is-short) .deck-workspace {
  grid-row: 3;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.deck-builder.is-compact:not(.is-short) .deck-workspace.fill-viewport .md-panel {
  height: 100%;
}

.deck-builder.is-compact:not(.is-short) .deck-pane-compact .deck-board {
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.deck-builder.is-compact:not(.is-short) .deck-pane-compact .main-deck-grid {
  flex: none;
  overflow: visible;
}

.deck-builder.is-compact:not(.is-short) .extra-deck-grid {
  max-height: none;
  overflow: visible;
}

.deck-builder.is-compact:not(.is-short) .collection-sprite-grid {
  overscroll-behavior: contain;
}

.land-rail.is-port {
  grid-row: 2;
  flex: none;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  justify-content: flex-start;
  width: auto;
  max-width: 100%;
  min-height: 0;
  overflow: visible;
  gap: 6px;
  position: sticky;
  top: 26px;
  z-index: 4;
  padding-bottom: 8px;
  background: var(--md-sys-color-surface);
}

.land-rail.is-port .rail-stats,
.land-rail.is-port .rail-actions {
  display: contents;
}

.land-rail.is-port .land-btn {
  width: auto;
  flex: 0 1 auto;
  min-height: 36px;
  padding: 0 12px;
  font-size: 13px;
}

.land-rail.is-port .rail-btn {
  width: 40px;
  min-width: 40px;
  flex: 0 0 40px;
  min-height: 36px;
}

.land-rail.is-port .rail-btn.undo {
  width: auto;
  min-width: 0;
  flex: 0 1 auto;
  padding: 0 10px;
}

.land-rail.is-port .rail-count {
  width: auto;
}

.deck-builder.is-compact .main-deck-grid {
  grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
  align-content: start;
  min-height: 8rem;
  margin-bottom: 6px;
  padding: 6px;
  gap: 8px;
}

.deck-builder.is-compact .extra-block {
  margin-bottom: 0;
  flex-shrink: 0;
}

.deck-builder.is-compact .extra-label {
  margin-bottom: 4px;
  font-size: 11px;
}

.deck-builder.is-compact .extra-deck-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 52px));
  justify-content: start;
  gap: 6px;
  min-height: 0;
  max-height: 5.5rem;
  overflow-y: auto;
  padding: 6px;
}

.deck-builder.is-compact .extra-deck-empty {
  grid-column: 1 / -1;
  padding: 6px 0;
  font-size: 12px;
}

.deck-builder.is-compact .empty-state {
  padding: 16px 8px;
}

.deck-builder.is-compact .empty-title {
  font-size: 16px;
  line-height: 22px;
}

.deck-builder.is-short .deck-editor-bar {
  gap: 0;
  padding-bottom: 4px;
}

.deck-builder.is-short .kicker {
  display: none;
}

.deck-builder.is-short .md-panel {
  padding: 8px;
}

.deck-builder.is-short .mobile-pane-tabs {
  margin-bottom: 0;
}

.deck-builder.is-short .deck-pane-body {
  gap: 6px;
}

.deck-builder.is-short .rail-count {
  font-size: 14px;
  line-height: 16px;
}

.deck-builder.is-short .rail-stat {
  min-width: 1.5rem;
  min-height: 1.25rem;
  padding: 0 4px;
  font-size: 11px;
}

.deck-builder.is-short .rail-actions {
  gap: 4px;
}

.deck-builder.is-short .rail-btn {
  min-height: 32px;
  border-radius: 8px;
}

.deck-builder.is-short .rail-btn svg {
  width: 16px;
  height: 16px;
}

.deck-builder.is-short .main-deck-grid {
  grid-template-columns: repeat(auto-fill, minmax(48px, 1fr));
  gap: 8px;
  padding: 6px;
  margin-bottom: 4px;
}

.deck-builder.is-short .extra-deck-grid {
  grid-template-columns: repeat(auto-fill, minmax(36px, 42px));
  max-height: 3.75rem;
  padding: 4px;
}

@media (min-width: 1600px) {
  .collection-list {
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  }
}

@media (max-width: 1100px) {
  .main-deck-grid,
  .extra-deck-grid {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }
}

@media (max-width: 1024px) {
  .deck-builder-layout {
    flex-direction: column;
    align-items: stretch;
  }

  .card-viewer-rail {
    position: static;
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

.deck-list-overlay {
  position: fixed;
  inset: 0;
  z-index: 280;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding:
    max(12px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(12px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
}

.deck-list-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
}

.deck-list-toggle {
  display: inline-flex;
  padding: 2px;
  border-radius: 10px;
  background: var(--md-sys-color-surface-container-high);
}

.deck-list-toggle button {
  min-height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.deck-list-toggle button.on {
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.deck-list-kicker {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--md-sys-color-on-surface-variant);
}

.deck-list-share {
  min-height: 36px;
  padding: 0 14px;
}

.deck-list-stage {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.deck-list-sheet {
  width: 100%;
}

.deck-list-sheet h2 {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
  text-align: center;
}

.deck-list-counts {
  margin: 2px 0 12px;
  text-align: center;
  font-size: 13px;
  line-height: 18px;
  color: var(--md-sys-color-on-surface-variant);
}

.deck-list-section + .deck-list-section {
  margin-top: 10px;
}

.deck-list-section h3 {
  margin: 0 0 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.deck-list-section .n {
  margin-right: 4px;
  font-variant-numeric: tabular-nums;
}

.deck-list-section .n.monster {
  color: #e2a06a;
}

.deck-list-section .n.spell {
  color: #81c784;
}

.deck-list-section .n.trap {
  color: #ce93d8;
}

.deck-list-section .n.extra {
  color: var(--md-sys-color-primary);
}

.deck-list-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.deck-list-grid li {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 52px;
  font-size: 14px;
  line-height: 18px;
}

.deck-list-art {
  width: 36px;
  height: 52px;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: 3px;
  background: var(--md-sys-color-surface-container-high);
}

.deck-list-sprite-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.deck-list-sprite-grid img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 59 / 86;
  object-fit: cover;
  border-radius: 4px;
  background: var(--md-sys-color-surface-container-high);
}

.deck-list-grid .qty {
  flex: 0 0 auto;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--md-sys-color-primary);
}

.deck-list-grid .name {
  min-width: 0;
  overflow-wrap: break-word;
}

.deck-list-overlay.short .deck-list-grid {
  grid-template-columns: 1fr 1fr 1fr;
}

@media (min-width: 800px) {
  .deck-list-grid {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .deck-list-sprite-grid {
    grid-template-columns: repeat(auto-fill, minmax(96px, 120px));
    justify-content: start;
  }
}

@media (max-width: 720px) {
  .page-header {
    flex-wrap: wrap;
  }

  .card-viewer-rail {
    display: none;
  }

  .mobile-pane-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 0;
  }

  .main-deck-grid,
  .extra-deck-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}
</style> 